/* eslint-disable no-dupe-keys */
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Radio, 
  RadioGroup, 
  FormControlLabel, 
  Button,
  Container,
  LinearProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Collapse,
  IconButton,
  Checkbox
} from '@mui/material';
import Sidebar from './Sidebar';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LaunchIcon from '@mui/icons-material/Launch';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import Tooltip from '@mui/material/Tooltip';
import { Link as RouterLink } from 'react-router-dom';
import { internalProblemPathFromLeetcodeUrl, isInternalRoute } from '../utils/problemLinks';

// Updated data structure to include LeetCode problems and their related questions
const quizzesByTopic = {
  'arrays-and-hashing': {
    title: 'Arrays & Hashing',
    problems: [
      {
        id: 1,
        name: 'Two Sum',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/two-sum',
        questions: [
          {
            question: 'What is the time complexity of the optimal solution for Two Sum?',
            options: ['O(n²)', 'O(n log n)', 'O(n)', 'O(1)'],
            correctAnswer: 2,
            explanation: 'Using a hash map allows us to solve Two Sum in O(n) time complexity.'
          },
          {
            question: 'Which data structure is most efficient for solving Two Sum?',
            options: ['Array', 'Hash Map', 'Binary Tree', 'Stack'],
            correctAnswer: 1,
            explanation: 'A hash map allows us to store complements and look them up in O(1) time.'
          }
        ]
      },
      // Add more problems for this topic...
    ]
  },
  'two-pointer': {
    title: 'Two Pointers',
    problems: [
      {
        id: 1,
        name: 'Two Pointers Basics',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/two-sum-ii-input-array-is-sorted/',
        questions: [
          {
            question: 'What is the main idea behind the two pointers technique?',
            options: [
              'Use two indices to scan the array from both ends, discarding impossible pairs based on the problem condition',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Two pointers allow us to efficiently find pairs or triplets by moving pointers based on the problem condition.'
          },
          {
            question: 'What is the time and space complexity of the two pointers approach?',
            options: [
              'O(n) time, O(1) space', 'O(n^2) time, O(1) space', 'O(n) time, O(n) space', 'O(n^2) time, O(n) space'
            ],
            correctAnswer: 0,
            explanation: 'Each pointer moves at most n times, and we use only a few integer variables.'
          },
          {
            question: 'Why does the two pointers approach work for sorted arrays?',
            options: [
              'Because we can discard large parts of the array based on the sorted order',
              'Because it is faster',
              'Because it uses a stack',
              'Because it uses a queue'
            ],
            correctAnswer: 0,
            explanation: 'The sorted order allows us to make decisions about which pointer to move.'
          }
        ]
      },
      {
        id: 2,
        name: 'Two Pointers Template',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/two-sum-ii-input-array-is-sorted/',
        questions: [
          {
            question: 'What is the general template for the two pointers approach?',
            options: [
              'Initialize i = 0, j = n-1, and move i or j based on the condition',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'The template is to initialize two pointers and move them based on the problem condition.'
          },
          {
            question: 'How do you generalize two pointers to triplets or higher?',
            options: [
              'Fix one index and use two pointers on the rest',
              'Use three pointers',
              'Sort the array',
              'Use a stack'
            ],
            correctAnswer: 0,
            explanation: 'Fix one index and use two pointers on the remaining indices.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Two Pointers Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/3sum/',
        questions: [
          {
            question: 'How do you solve the Two Sum II problem using two pointers?',
            options: [
              'Move i forward if the sum is too small, j backward if the sum is too large',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Move i forward if the sum is too small, j backward if the sum is too large.'
          },
          {
            question: 'How do you solve the 3Sum problem using two pointers?',
            options: [
              'Fix one index and use two pointers on the rest, skipping duplicates',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Fix one index and use two pointers on the rest, skipping duplicates.'
          },
          {
            question: 'How do you solve the Valid Triangle Number problem using two pointers?',
            options: [
              'Fix the largest side and use two pointers to count valid pairs',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Fix the largest side and use two pointers to count valid pairs.'
          }
        ]
      },
      {
        id: 4,
        name: 'Advanced Two Pointers Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/3sum-closest/',
        questions: [
          {
            question: 'How do you solve the 3Sum Closest problem using two pointers?',
            options: [
              'Fix one index and use two pointers to find the closest sum',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Fix one index and use two pointers to find the closest sum.'
          },
          {
            question: 'How do you solve the Maximize Greatness problem using two pointers?',
            options: [
              'Sort the array and use two pointers from the beginning to match values',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Sort the array and use two pointers from the beginning to match values.'
          }
        ]
      }
    ]
  },
  'binary-search': {
    title: 'Binary Search',
    problems: [
      {
        id: 1,
        name: 'Binary Search Basics',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/binary-search',
        questions: [
          {
            question: 'What is the time complexity of binary search?',
            options: ['O(n)', 'O(log n)', 'O(n log n)', 'O(1)'],
            correctAnswer: 1,
            explanation: 'Binary search reduces the search space by half in each iteration, leading to logarithmic time complexity.'
          },
          {
            question: 'Why do we use mid = left + (right-left)//2 instead of mid = (left+right)//2?',
            options: [
              'It\'s faster',
              'It prevents integer overflow',
              'It\'s more accurate',
              'It\'s easier to read'
            ],
            correctAnswer: 1,
            explanation: 'When left and right are large numbers, left+right might exceed INT_MAX, causing overflow.'
          },
          {
            question: 'What is a monotonic function in the context of binary search?',
            options: [
              'A function that always increases',
              'A function that always decreases',
              'A function that is either all True then all False, or all False then all True',
              'A function that has no duplicates'
            ],
            correctAnswer: 2,
            explanation: 'Binary search requires a monotonic function to work correctly, as it needs a clear transition point.'
          }
        ]
      },
      {
        id: 2,
        name: 'Binary Search Templates',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/find-first-and-last-position-of-element-in-sorted-array',
        questions: [
          {
            question: 'What are the two main binary search templates?',
            options: [
              'Min and Max templates',
              'Left and Right templates',
              'First and Last templates',
              'Start and End templates'
            ],
            correctAnswer: 0,
            explanation: 'The Min template finds the first occurrence of a condition, while the Max template finds the last occurrence.'
          },
          {
            question: 'When do we use the Min template?',
            options: [
              'When we want to find the last occurrence',
              'When we want to find the first occurrence',
              'When we want to find any occurrence',
              'When we want to find the middle occurrence'
            ],
            correctAnswer: 1,
            explanation: 'The Min template is used when we want to find the first occurrence of a condition (first True in FFF...TTT pattern).'
          }
        ]
      },
      {
        id: 3,
        name: 'Find First and Last Position',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/find-first-and-last-position-of-element-in-sorted-array',
        questions: [
          {
            question: 'How can we find both the first and last position of an element in a sorted array?',
            options: [
              'Use two separate binary searches',
              'Use linear search',
              'Use a hash map',
              'Use a single binary search'
            ],
            correctAnswer: 0,
            explanation: 'We use the Min template to find the first occurrence and the Max template to find the last occurrence.'
          },
          {
            question: 'What is the time complexity of finding first and last position?',
            options: ['O(n)', 'O(log n)', 'O(n log n)', 'O(1)'],
            correctAnswer: 1,
            explanation: 'We perform two binary searches, each taking O(log n) time.'
          }
        ]
      },
      {
        id: 4,
        name: 'Search Insert Position',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/search-insert-position',
        questions: [
          {
            question: 'What is the key insight for Search Insert Position?',
            options: [
              'We need to find the exact position',
              'The answer is always the last position',
              'The answer is the first position where nums[mid] >= target',
              'We need to check both sides'
            ],
            correctAnswer: 2,
            explanation: 'The first position where nums[mid] >= target is where we would insert the target to maintain sorted order.'
          }
        ]
      },
      {
        id: 5,
        name: 'Find Peak Element',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/find-peak-element',
        questions: [
          {
            question: 'Why can we use binary search to find a peak element?',
            options: [
              'Because the array is sorted',
              'Because we can compare adjacent elements to determine which half to search',
              'Because we need to find the maximum element',
              'Because we need to find the minimum element'
            ],
            correctAnswer: 1,
            explanation: 'If nums[mid] < nums[mid+1], the peak must be in the right half. Otherwise, it must be in the left half.'
          },
          {
            question: 'What is the time complexity of finding a peak element?',
            options: ['O(n)', 'O(log n)', 'O(n log n)', 'O(1)'],
            correctAnswer: 1,
            explanation: 'We use binary search to find the peak, which takes O(log n) time.'
          }
        ]
      }
    ]
  },
  'backtracking': {
    title: 'Backtracking',
    problems: [
      {
        id: 1,
        name: 'Backtracking Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/backtracking/',
        questions: [
          { question: 'What is the main purpose of backtracking algorithms?', options: ['To find the shortest path in a graph','To enumerate all possible solutions by exploring all choices','To sort an array efficiently','To optimize dynamic programming solutions'], correctAnswer: 1, explanation: 'Backtracking is used to enumerate all possible solutions by exploring every choice at each step.' },
          { question: 'What is the time complexity of generating all subsets of a set of size n using backtracking?', options: ['O(n)', 'O(n^2)', 'O(2^n)', 'O(n*2^n)'], correctAnswer: 3, explanation: 'There are 2^n subsets, and each subset can have up to n elements, so the total time is O(n*2^n).' },
          { question: 'Which of the following is NOT a typical use case for backtracking?', options: ['Generating all permutations','Finding all subsets','Solving the Two Sum problem in O(n)','Solving the N-Queens problem'], correctAnswer: 2, explanation: 'Two Sum can be solved in O(n) with a hash map, not by backtracking.' },
          { question: 'In the backtracking template, why do we use curr.copy() when appending to the result?', options: ['To avoid integer overflow','To ensure each result is a separate list','To improve performance','To sort the results'], correctAnswer: 1, explanation: 'Without curr.copy(), all results would reference the same list object, which changes as backtracking continues.' }
        ]
      },
      {
        id: 2,
        name: 'Subsets & Duplicates',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/subsets-ii/',
        questions: [
          { question: 'What is the difference between combinations and permutations in backtracking?', options: ['Combinations consider order, permutations do not','Permutations consider order, combinations do not','Both consider order','Neither consider order'], correctAnswer: 1, explanation: 'Permutations consider the order of elements, while combinations do not.' },
          { question: 'How do you avoid duplicate subsets when the input array contains duplicates?', options: ['Use a set and sort each subset before adding','Use a hash map','Ignore duplicates','Use a queue'], correctAnswer: 0, explanation: 'Sorting and using a set ensures that duplicate subsets are not added.' },
          { question: 'What is the key insight for generating all subsets (the power set) using backtracking?', options: ['At each step, either include or exclude the current element','Always include the current element','Sort the array before recursion','Use a stack to store all subsets'], correctAnswer: 0, explanation: 'For each element, you have two choices: include it in the current subset or not. This leads to 2^n possible subsets.' }
        ]
      },
      {
        id: 3,
        name: 'Permutations',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/permutations/',
        questions: [
          { question: 'What is the key insight for generating all permutations using backtracking?', options: ['Swap the current element with each possible choice and recurse on the next index','Always use the first element','Sort the array before recursion','Use a queue to store all permutations'], correctAnswer: 0, explanation: 'By swapping the current element with each possible choice and recursing, you generate all possible orderings (permutations).' },
          { question: 'What is the general template for a backtracking function?', options: ['Iterate through all elements and return the first valid result','Recursively try all choices at each step, backtrack after each choice, and add valid results to the answer list','Use a hash map to store results','Sort the input and use binary search'], correctAnswer: 1, explanation: 'The backtracking template involves recursion, trying all choices at each step, and undoing the choice (backtracking) after recursion.' }
        ]
      },
      {
        id: 4,
        name: 'Combination Sum',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/combination-sum/',
        questions: [
          { question: 'Which problem is solved by allowing repeated use of the same element in combinations?', options: ['Combination Sum','Permutations','Subsets','Palindrome Partitioning'], correctAnswer: 0, explanation: 'Combination Sum allows repeated use of the same element to reach the target sum.' },
          { question: 'How does the backtracking template change for Combination Sum problems?', options: ['You allow repeated use of the same element by recursing on the same index, and prune when the current sum exceeds the target','Sort the array before recursion','Use a hash map to store sums','Always use each element only once'], correctAnswer: 0, explanation: 'You can use the same element multiple times, so you recurse on the same index instead of moving to the next.' },
          { question: 'What is the key insight for solving Combination Sum with backtracking?', options: ['Allow repeated use of the same element by not incrementing the index in recursion; prune when the current sum exceeds the target','Sort the array before recursion','Use a hash map to store sums','Always use each element only once'], correctAnswer: 0, explanation: 'In Combination Sum, you can use the same element multiple times, so you recurse on the same index instead of moving to the next.' }
        ]
      },
      {
        id: 5,
        name: 'Pruning & Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/n-queens/',
        questions: [
          { question: 'What is a common optimization in backtracking to reduce unnecessary computation?', options: ['Pruning branches that cannot lead to a valid solution','Using bubble sort','Increasing recursion depth','Using a stack instead of recursion'], correctAnswer: 0, explanation: 'Pruning helps avoid exploring branches that cannot possibly yield a valid solution.' },
          { question: 'Which of the following problems is best solved by backtracking?', options: ['Finding the maximum subarray sum','Generating all valid parentheses combinations','Merging two sorted lists','Finding the minimum in a rotated array'], correctAnswer: 1, explanation: 'Generating all valid parentheses is a classic backtracking problem.' }
        ]
      },
      {
        id: 6,
        name: 'Palindrome Partitioning',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/palindrome-partitioning/',
        questions: [
          { question: 'What is the key insight for Palindrome Partitioning using backtracking?', options: ['At each step, try all substrings starting at the current index and recurse only if the substring is a palindrome','Sort the string before recursion','Use a hash map to store palindromes','Always partition at the middle of the string'], correctAnswer: 0, explanation: 'For palindrome partitioning, you try every possible substring starting at the current index, and only recurse if the substring is a palindrome.' }
        ]
      }
    ]
  },
  'linked-list': {
    title: 'Linked List',
    problems: [
      {
        id: 1,
        name: 'Linked List Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/linked-list/',
        questions: [
          {
            question: 'What is a linked list',
            options: [
              'A linear data structure where each node contains data and a reference to the next node',
              'A tree structure',
              'A hash map',
              'A stack'
            ],
            correctAnswer: 0,
            explanation: 'A linked list is a linear data structure where each node contains data and a reference to the next node.'
          },
          {
            question: 'What is the main advantage of a linked list over an array',
            options: [
              'Efficient insertions and deletions without shifting elements',
              'Faster random access',
              'Less memory usage',
              'Easier to sort'
            ],
            correctAnswer: 0,
            explanation: 'Linked lists allow O(1) insertions and deletions when you have a reference to the node.'
          },
          {
            question: 'What is the dummy node pattern and why is it useful',
            options: [
              'A sentinel node placed at the start to simplify edge cases',
              'A node that stores extra data',
              'A node that speeds up traversal',
              'A node that reduces memory usage'
            ],
            correctAnswer: 0,
            explanation: 'A dummy node helps avoid special cases when the head of the list might change.'
          }
        ]
      },
      {
        id: 2,
        name: 'Fast and Slow Pointers',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/linked-list-cycle/',
        questions: [
          {
            question: 'How does the fast slow pointer technique work',
            options: [
              'One pointer moves faster than the other to find the middle or detect cycles',
              'Both pointers move at the same speed',
              'One pointer moves backward',
              'Both pointers start at the end'
            ],
            correctAnswer: 0,
            explanation: 'One pointer moves faster than the other; it is used to find the middle or kth last node, or to detect cycles.'
          },
          {
            question: 'How do you find the middle node of a linked list',
            options: [
              'Use fast and slow pointers; when fast reaches the end, slow is at the middle',
              'Use a stack',
              'Use a hash map',
              'Sort the list'
            ],
            correctAnswer: 0,
            explanation: 'Use fast and slow pointers; when fast reaches the end, slow is at the middle.'
          }
        ]
      },
      {
        id: 3,
        name: 'Reversal and Deletion',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/reverse-linked-list/',
        questions: [
          {
            question: 'What is the iterative template for reversing a linked list',
            options: [
              'Iterate through the list, changing each nodes next pointer to point to the previous node',
              'Use recursion only',
              'Sort the list',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Iterate through the list, changing each nodes next pointer to point to the previous node.'
          },
          {
            question: 'How do you remove the nth node from the end of a linked list',
            options: [
              'Use a dummy node and two pointers spaced n plus 1 apart; when the fast pointer reaches the end, the slow pointer is before the node to remove',
              'Use a stack',
              'Sort the list',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Use a dummy node and two pointers spaced n plus 1 apart; when the fast pointer reaches the end, the slow pointer is before the node to remove.'
          }
        ]
      },
      {
        id: 4,
        name: 'Advanced',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/copy-list-with-random-pointer/',
        questions: [
          {
            question: 'How do you delete a node in a singly linked list when only given access to that node',
            options: [
              'Copy the value from the next node and delete the next node instead',
              'Remove the node from the head',
              'Set the node to null',
              'It is not possible'
            ],
            correctAnswer: 0,
            explanation: 'Copy the value from the next node and delete the next node instead.'
          }
        ]
      }
    ]
  },
  'sorted-list': {
    title: 'Sorted List',
    problems: [
      {
        id: 1,
        name: 'SortedList Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://grantjenks.com/docs/sortedcontainers/sortedlist.html',
        questions: [
          {
            question: 'What is a SortedList in Python?',
            options: [
              'A list that is always kept sorted and supports fast insertion and deletion',
              'A regular list that is sorted once',
              'A heap data structure',
              'A dictionary with sorted keys'
            ],
            correctAnswer: 0,
            explanation: 'A SortedList is a data structure that maintains its elements in sorted order and supports O(log n) insertion and deletion.'
          },
          {
            question: 'Which library provides the SortedList in Python?',
            options: [
              'heapq', 'bisect', 'sortedcontainers', 'collections'
            ],
            correctAnswer: 2,
            explanation: 'SortedList is provided by the sortedcontainers library.'
          },
          {
            question: 'What does sl[i] return for a SortedList sl?',
            options: [
              'The ith value in the sorted list',
              'The ith value in the original insertion order',
              'The maximum value',
              'The minimum value'
            ],
            correctAnswer: 0,
            explanation: 'sl[i] returns the ith value in the sorted order.'
          }
        ]
      },
      {
        id: 2,
        name: 'Bisect Methods',
        difficulty: 'Medium',
        leetcodeUrl: 'https://grantjenks.com/docs/sortedcontainers/sortedlist.html',
        questions: [
          {
            question: 'What does bisect_left(sl, x) return?',
            options: [
              'The last index with value < x',
              'The first index with value >= x',
              'The first index with value > x',
              'The index of the maximum value'
            ],
            correctAnswer: 1,
            explanation: 'bisect_left(sl, x) returns the first index in sl with value >= x.'
          },
          {
            question: 'How do you find the largest number less than x in a SortedList sl?',
            options: [
              'bisect_left(sl, x)',
              'bisect_left(sl, x) - 1',
              'bisect_right(sl, x)',
              'bisect_right(sl, x) - 1'
            ],
            correctAnswer: 1,
            explanation: 'bisect_left(sl, x) - 1 gives the last index with value < x.'
          },
          {
            question: 'How do you find the largest number less than or equal to x in a SortedList sl?',
            options: [
              'bisect_left(sl, x) - 1',
              'bisect_right(sl, x) - 1',
              'bisect_left(sl, x)',
              'bisect_right(sl, x)'
            ],
            correctAnswer: 1,
            explanation: 'bisect_right(sl, x) - 1 gives the last index with value <= x.'
          },
          {
            question: 'How do you find the smallest number greater than or equal to x in a SortedList sl?',
            options: [
              'bisect_left(sl, x)',
              'bisect_left(sl, x) - 1',
              'bisect_right(sl, x)',
              'bisect_right(sl, x) - 1'
            ],
            correctAnswer: 0,
            explanation: 'bisect_left(sl, x) gives the first index with value >= x.'
          },
          {
            question: 'How do you find the smallest number greater than x in a SortedList sl?',
            options: [
              'bisect_left(sl, x)',
              'bisect_right(sl, x)',
              'bisect_left(sl, x) - 1',
              'bisect_right(sl, x) - 1'
            ],
            correctAnswer: 1,
            explanation: 'bisect_right(sl, x) gives the first index with value > x.'
          }
        ]
      },
      {
        id: 3,
        name: 'Tuple Sorting',
        difficulty: 'Medium',
        leetcodeUrl: 'https://grantjenks.com/docs/sortedcontainers/sortedlist.html',
        questions: [
          {
            question: 'How are tuples sorted in a SortedList?',
            options: [
              'By the sum of their elements',
              'By the first element, then by the next as a tiebreaker',
              'By the last element only',
              'Randomly'
            ],
            correctAnswer: 1,
            explanation: 'Tuples are sorted lexicographically: first by the first element, then by the next as a tiebreaker.'
          }
        ]
      },
      {
        id: 4,
        name: 'Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://grantjenks.com/docs/sortedcontainers/sortedlist.html',
        questions: [
          {
            question: 'What is a common use case for SortedList in algorithm problems?',
            options: [
              'Finding the kth largest element efficiently',
              'Maintaining a sliding window of sorted values',
              'Efficiently finding predecessors and successors',
              'All of the above'
            ],
            correctAnswer: 3,
            explanation: 'SortedList is commonly used for all these use cases in competitive programming and interviews.'
          }
        ]
      }
    ]
  },
  'interval': {
    title: 'Interval',
    problems: [
      {
        id: 1,
        name: 'Interval Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/interval/',
        questions: [
          {
            question: 'What is an interval?',
            options: [
              'A pair of integers representing a range, such as [start, end]',
              'A single integer',
              'A sorted array',
              'A hash map'
            ],
            correctAnswer: 0,
            explanation: 'An interval is a pair of integers representing a range.'
          },
          {
            question: 'How do you determine if two intervals [a,b] and [c,d] overlap?',
            options: [
              'They overlap if max(a,c) <= min(b,d)',
              'They overlap if a == c',
              'They overlap if b < c',
              'They overlap if a > d'
            ],
            correctAnswer: 0,
            explanation: 'The intervals overlap if max(a,c) <= min(b,d).'
          },
          {
            question: 'What is the key insight for solving most interval problems efficiently?',
            options: [
              'Sort the intervals and use greedy algorithms',
              'Use a stack',
              'Use a hash map',
              'Use dynamic programming'
            ],
            correctAnswer: 0,
            explanation: 'Sort the intervals and use greedy algorithms.'
          }
        ]
      },
      {
        id: 2,
        name: 'Merging & Overlap',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/merge-intervals/',
        questions: [
          {
            question: 'How do you merge overlapping intervals?',
            options: [
              'Sort by start, then merge if the current start is <= the last end',
              'Sort by end, then merge if the current end is >= the last start',
              'Use a stack',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Sort by start, then merge if the current start is <= the last end.'
          },
          {
            question: 'How do you check if a person can attend all meetings given a list of intervals?',
            options: [
              'Check if any intervals overlap',
              'Check if all intervals are the same length',
              'Check if the intervals are sorted',
              'Check if the number of intervals is even'
            ],
            correctAnswer: 0,
            explanation: 'If any intervals overlap, the person cannot attend all meetings.'
          },
          {
            question: 'What is the minimum number of meeting rooms required for a set of intervals?',
            options: [
              'The maximum number of overlapping intervals at any point',
              'The minimum number of intervals',
              'The average number of intervals',
              'The number of unique start times'
            ],
            correctAnswer: 0,
            explanation: 'The maximum number of overlapping intervals at any point.'
          }
        ]
      },
      {
        id: 3,
        name: 'Line Sweep & Meeting Rooms',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/meeting-rooms-ii/',
        questions: [
          {
            question: 'What is the line sweep algorithm used for in interval problems?',
            options: [
              'Counting overlapping intervals',
              'Finding the maximum number of concurrent intervals',
              'Efficiently handling a mix of intervals and point queries',
              'All of the above'
            ],
            correctAnswer: 3,
            explanation: 'Line sweep is used for all of these in interval problems.'
          },
          {
            question: 'How do you find the intersection of two lists of intervals?',
            options: [
              'Use two pointers and add the overlap if max(a,c) <= min(b,d)',
              'Sort both lists and merge',
              'Use a stack',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Use two pointers and add the overlap if max(a,c) <= min(b,d).'
          },
          {
            question: 'How do you remove covered intervals?',
            options: [
              'Sort by start, then by decreasing end, and skip intervals covered by the previous',
              'Sort by end, then by increasing start',
              'Use a stack',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Sort by start, then by decreasing end, and skip intervals covered by the previous.'
          }
        ]
      },
      {
        id: 4,
        name: 'Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/interval/',
        questions: [
          {
            question: 'What is the key insight for the My Calendar series of problems?',
            options: [
              'Use line sweep or binary search to efficiently track overlapping events',
              'Use a stack',
              'Use a hash map',
              'Use dynamic programming'
            ],
            correctAnswer: 0,
            explanation: 'Line sweep and binary search are key for efficiently tracking overlapping events.'
          },
          {
            question: 'What does the running sum represent in the line sweep algorithm?',
            options: [
              'The number of active intervals at a given point',
              'The number of intervals processed',
              'The number of unique start times',
              'The number of unique end times'
            ],
            correctAnswer: 0,
            explanation: 'The running sum is the number of active intervals at a given point.'
          },
          {
            question: 'What is a real-world application of interval problems?',
            options: [
              'Meeting room scheduling',
              'Resource allocation',
              'Time series analysis',
              'All of the above'
            ],
            correctAnswer: 3,
            explanation: 'All of these are real-world applications of interval problems.'
          }
        ]
      }
    ]
  },
  'greedy': {
    title: 'Greedy',
    problems: [
      {
        id: 1,
        name: 'Greedy Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/greedy/',
        questions: [
          {
            question: 'What is the greedy algorithmic paradigm?',
            options: [
              'Making locally optimal choices at each step, hoping to reach a global optimum',
              'Using dynamic programming',
              'Using recursion',
              'Using a stack'
            ],
            correctAnswer: 0,
            explanation: 'Greedy makes locally optimal choices at each step.'
          },
          {
            question: 'Why can greedy algorithms sometimes fail?',
            options: [
              'They do not consider future consequences and may miss the global optimum',
              'They use too much memory',
              'They are too slow',
              'They always work'
            ],
            correctAnswer: 0,
            explanation: 'Greedy may miss the global optimum by not considering future consequences.'
          },
          {
            question: 'How can you prove a greedy algorithm is correct?',
            options: [
              'Exchange argument or greedy stays ahead',
              'By running it on a few test cases',
              'By using recursion',
              'By using a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Proofs like exchange argument or greedy stays ahead are used to show correctness.'
          }
        ]
      },
      {
        id: 2,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/jump-game/',
        questions: [
          {
            question: 'What is the greedy approach for Jump Game I?',
            options: [
              'Track the maximum reachable index as you iterate through the array',
              'Use dynamic programming',
              'Use a stack',
              'Sort the array'
            ],
            correctAnswer: 0,
            explanation: 'Track the maximum reachable index as you iterate through the array.'
          },
          {
            question: 'What is the greedy approach for Jump Game II?',
            options: [
              'Track the end of the current jump range and increment jumps when you reach it',
              'Use dynamic programming',
              'Use a stack',
              'Sort the array'
            ],
            correctAnswer: 0,
            explanation: 'Track the end of the current jump range and increment jumps when you reach it.'
          },
          {
            question: 'What is the greedy approach for Container With Most Water?',
            options: [
              'Use two pointers and always move the pointer at the shorter line',
              'Sort the array',
              'Use a stack',
              'Use dynamic programming'
            ],
            correctAnswer: 0,
            explanation: 'Use two pointers and always move the pointer at the shorter line.'
          },
          {
            question: 'What is the optimal time complexity for Container With Most Water?',
            options: [
              'O(n)', 'O(n^2)', 'O(n log n)', 'O(log n)'
            ],
            correctAnswer: 0,
            explanation: 'O(n) using the two-pointer greedy approach.'
          }
        ]
      },
      {
        id: 3,
        name: 'Advanced Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/trapping-rain-water/',
        questions: [
          {
            question: 'What is the greedy approach for Trapping Rain Water (O(1) space)?',
            options: [
              'Use two pointers and track max heights from both ends',
              'Sort the array',
              'Use a stack',
              'Use dynamic programming'
            ],
            correctAnswer: 0,
            explanation: 'Two pointers and tracking max heights from both ends allows O(1) space.'
          },
          {
            question: 'What is the greedy approach for maximizing profit in task assignment?',
            options: [
              'Always assign the highest profit available task to each worker',
              'Assign tasks randomly',
              'Sort tasks by skill',
              'Use dynamic programming'
            ],
            correctAnswer: 0,
            explanation: 'Assign the highest profit available task to each worker for maximum profit.'
          },
          {
            question: 'What is the greedy approach for Furthest Building You Can Reach?',
            options: [
              'Use ladders for the largest jumps and bricks for smaller ones',
              'Use bricks for all jumps',
              'Use ladders for all jumps',
              'Sort the buildings by height'
            ],
            correctAnswer: 0,
            explanation: 'Use ladders for the largest jumps and bricks for smaller ones, possibly swapping as you go.'
          }
        ]
      },
      {
        id: 4,
        name: 'Proofs & Pitfalls',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/greedy/',
        questions: [
          {
            question: 'What is a common pitfall when using greedy algorithms?',
            options: [
              'Assuming greedy always gives the optimal solution',
              'Using too much memory',
              'Being too slow',
              'Not using enough recursion'
            ],
            correctAnswer: 0,
            explanation: 'Greedy does not always guarantee the optimal solution; always check if it applies.'
          }
        ]
      }
    ]
  },
  'divide-and-conquer': {
    title: 'Divide and Conquer',
    problems: [
      {
        id: 1,
        name: 'Divide and Conquer Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/divide-and-conquer/',
        questions: [
          {
            question: 'What is the main idea behind divide and conquer algorithms?',
            options: [
              'Make locally optimal choices at each step',
              'Divide the problem into subproblems, solve them recursively, and combine the results',
              'Use dynamic programming',
              'Use a stack to simulate recursion'
            ],
            correctAnswer: 1,
            explanation: 'Divide and conquer splits the problem, solves subproblems recursively, and joins the results.'
          },
          {
            question: 'What is the "join" step in divide and conquer?',
            options: [
              'The base case',
              'The step where you combine results from subproblems',
              'The step where you split the problem',
              'The step where you sort the input'
            ],
            correctAnswer: 1,
            explanation: 'The join step combines the results of recursive subproblems.'
          },
          {
            question: 'Which theorem is commonly used to analyze the time complexity of divide and conquer algorithms?',
            options: [
              'Master Theorem',
              'Pigeonhole Principle',
              'Bayes Theorem',
              'Fermat\'s Little Theorem'
            ],
            correctAnswer: 0,
            explanation: 'The Master Theorem is used to analyze recurrences in divide and conquer.'
          }
        ]
      },
      {
        id: 2,
        name: 'Merge Sort & Inversions',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/sort-an-array/',
        questions: [
          {
            question: 'What is the time complexity of merge sort?',
            options: [
              'O(n)', 'O(n^2)', 'O(n log n)', 'O(log n)'
            ],
            correctAnswer: 2,
            explanation: 'Merge sort splits the array and merges in O(n log n) time.'
          },
          {
            question: 'What is the key step in merging two sorted arrays?',
            options: [
              'Compare the smallest elements and add the smaller one to the result',
              'Sort both arrays',
              'Use a stack',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Compare the smallest elements and add the smaller one to the result.'
          },
          {
            question: 'How can you count inversions in an array efficiently?',
            options: [
              'Brute force O(n^2)',
              'Use merge sort and count cross-inversions during the merge step',
              'Sort the array',
              'Use a hash map'
            ],
            correctAnswer: 1,
            explanation: 'Count inversions using merge sort and count cross-inversions during merge.'
          },
          {
            question: 'What is a cross-inversion in the context of inversion counting?',
            options: [
              'An inversion within the left subarray',
              'An inversion within the right subarray',
              'An inversion between the left and right subarrays',
              'An inversion in a sorted array'
            ],
            correctAnswer: 2,
            explanation: 'A cross-inversion is between the left and right subarrays.'
          }
        ]
      },
      {
        id: 3,
        name: 'Quad Trees & Grids',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/construct-quad-tree/',
        questions: [
          {
            question: 'What is the base case for most divide and conquer algorithms?',
            options: [
              'When the input size is 1',
              'When the input is sorted',
              'When the input is empty',
              'When the input is a palindrome'
            ],
            correctAnswer: 0,
            explanation: 'The base case is usually when the input size is 1.'
          },
          {
            question: 'What is the main idea behind constructing a quad tree?',
            options: [
              'Recursively divide the grid into four quadrants and build subtrees',
              'Sort the grid',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Quad trees are built by recursively dividing the grid into four quadrants.'
          },
          {
            question: 'What is the time complexity of constructing a quad tree for an n x n grid?',
            options: [
              'O(n)', 'O(n log n)', 'O(n^2)', 'O(n^4)'
            ],
            correctAnswer: 2,
            explanation: 'Constructing a quad tree for an n x n grid is O(n^2).' 
          },
          {
            question: 'What is the key insight for filling a special grid in the divide and conquer problem?',
            options: [
              'Recursively fill each quadrant and increment values by offsets',
              'Sort the grid',
              'Use a stack',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Recursively fill each quadrant and increment values by offsets.'
          }
        ]
      },
      {
        id: 4,
        name: 'Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/divide-and-conquer/',
        questions: [
          {
            question: 'Why is divide and conquer a powerful technique in software engineering?',
            options: [
              'It allows you to break down complex problems into manageable subproblems',
              'It always gives the fastest solution',
              'It uses the most memory',
              'It is only useful for sorting'
            ],
            correctAnswer: 0,
            explanation: 'Divide and conquer helps break down complex problems into manageable subproblems.'
          }
        ]
      }
    ]
  },
  'palindrome': {
    title: 'Palindrome',
    problems: [
      {
        id: 1,
        name: 'Palindrome Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/palindrome/',
        questions: [
          {
            question: 'What is a palindrome?',
            options: [
              'A string that reads the same forward and backward',
              'A string with all unique characters',
              'A string with only vowels',
              'A string with only consonants'
            ],
            correctAnswer: 0,
            explanation: 'A palindrome reads the same forward and backward.'
          },
          {
            question: 'What is the recursive structure of a palindrome?',
            options: [
              's[0] == s[-1] and the substring s[1:-1] is a palindrome',
              's[0] == s[1]',
              's is sorted',
              's is empty'
            ],
            correctAnswer: 0,
            explanation: 'A palindrome is defined recursively as s[0] == s[-1] and s[1:-1] is a palindrome.'
          },
          {
            question: 'What is a common interview problem involving palindromes?',
            options: [
              'Longest palindromic substring',
              'Valid palindrome with deletions',
              'Counting palindromic substrings',
              'All of the above'
            ],
            correctAnswer: 3,
            explanation: 'All of these are common palindrome interview problems.'
          }
        ]
      },
      {
        id: 2,
        name: 'Palindrome Checking',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/valid-palindrome/',
        questions: [
          {
            question: 'How do you check if a string is a palindrome?',
            options: [
              'Compare characters from both ends moving toward the center',
              'Sort the string',
              'Count the number of vowels',
              'Check if the substring is sorted'
            ],
            correctAnswer: 0,
            explanation: 'Compare characters from both ends moving toward the center.'
          },
          {
            question: 'How do you check if a substring can be rearranged into a palindrome with at most k replacements?',
            options: [
              'Check if the number of odd frequency characters divided by 2 is <= k',
              'Sort the substring',
              'Count the number of vowels',
              'Check if the substring is sorted'
            ],
            correctAnswer: 0,
            explanation: 'If the number of odd frequency characters divided by 2 is <= k, it can be rearranged into a palindrome.'
          },
          {
            question: 'What is the time complexity of checking if a string is a palindrome?',
            options: [
              'O(1)', 'O(n)', 'O(n^2)', 'O(log n)'
            ],
            correctAnswer: 1,
            explanation: 'O(n), where n is the length of the string.'
          }
        ]
      },
      {
        id: 3,
        name: 'Partitioning & Substrings',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/palindrome-partitioning/',
        questions: [
          {
            question: 'What is the key insight for Palindrome Partitioning using backtracking?',
            options: [
              'At each step, try all substrings starting at the current index and recurse only if the substring is a palindrome',
              'Sort the string',
              'Use a stack',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'At each step, try all substrings starting at the current index and recurse only if the substring is a palindrome.'
          },
          {
            question: 'How do you count all palindromic substrings in a string?',
            options: [
              'Expand around every center and count palindromic substrings',
              'Sort the string',
              'Count the number of vowels',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'Expand around every center and count palindromic substrings.'
          }
        ]
      },
      {
        id: 4,
        name: 'Advanced/Code',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/different-palindromic-substrings/',
        questions: [
          {
            question: 'Write the Python code for checking if a string is a palindrome.',
            options: [
              'def isPalindrome(s): return s == s[::-1]',
              'def isPalindrome(s): return s == s[1:]',
              'def isPalindrome(s): return s == s[::2]',
              'def isPalindrome(s): return s == s[1:-1]'
            ],
            correctAnswer: 0,
            explanation: 'def isPalindrome(s): return s == s[::-1]'
          }
        ]
      }
    ]
  },
  'trie': {
    title: 'Trie',
    problems: [
      {
        id: 1,
        name: 'Trie Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/trie/',
        questions: [
          {
            question: 'What is a trie?',
            options: [
              'A tree-like data structure used to store strings, where each node represents a character',
              'A hash map',
              'A binary search tree',
              'A stack'
            ],
            correctAnswer: 0,
            explanation: 'A trie is a tree-like data structure for storing strings.'
          },
          {
            question: 'What is the main advantage of a trie over a hash map for string storage?',
            options: [
              'Efficient prefix queries and compact storage for shared prefixes',
              'Faster random access',
              'Less memory usage',
              'Easier to implement'
            ],
            correctAnswer: 0,
            explanation: 'Tries allow efficient prefix queries and compact storage for shared prefixes.'
          },
          {
            question: 'What is the root node in a trie?',
            options: [
              'The starting node that does not represent any character',
              'The node with the smallest value',
              'The node with the largest value',
              'The node with the most children'
            ],
            correctAnswer: 0,
            explanation: 'The root node is the starting node and does not represent any character.'
          }
        ]
      },
      {
        id: 2,
        name: 'Operations',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/implement-trie-prefix-tree/',
        questions: [
          {
            question: 'What are the main operations supported by a trie?',
            options: [
              'Insert, search, and startsWith (prefix search)',
              'Insert, delete, and update',
              'Push, pop, and top',
              'Sort, search, and delete'
            ],
            correctAnswer: 0,
            explanation: 'Insert, search, and startsWith are the main operations.'
          },
          {
            question: 'How do you insert a word into a trie?',
            options: [
              'Iterate through each character, creating nodes as needed, and mark the end of the word',
              'Sort the word and insert',
              'Insert at the root only',
              'Use a stack to insert'
            ],
            correctAnswer: 0,
            explanation: 'Iterate through each character, creating nodes as needed, and mark the end of the word.'
          },
          {
            question: 'How do you check if a word exists in a trie?',
            options: [
              'Traverse the trie following each character; if you reach the end and the node is marked as a word, it exists',
              'Check only the root node',
              'Sort the word and check',
              'Use a stack to check'
            ],
            correctAnswer: 0,
            explanation: 'Traverse the trie following each character; if you reach the end and the node is marked as a word, it exists.'
          }
        ]
      },
      {
        id: 3,
        name: 'Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/implement-trie-prefix-tree/',
        questions: [
          {
            question: 'What is a real-world application of tries?',
            options: [
              'Autocomplete',
              'Spellchecker',
              'Prefix search',
              'All of the above'
            ],
            correctAnswer: 3,
            explanation: 'Autocomplete, spellchecker, and prefix search are all real-world applications.'
          }
        ]
      },
      {
        id: 4,
        name: 'Complexity',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/implement-trie-prefix-tree/',
        questions: [
          {
            question: 'What is the space complexity of a trie storing n words of average length L?',
            options: [
              'O(n*L)',
              'O(n)',
              'O(L)',
              'O(1)'
            ],
            correctAnswer: 0,
            explanation: 'O(n*L) in the worst case.'
          }
        ]
      }
    ]
  },
  'prefix-sum': {
    title: 'Prefix Sums',
    problems: [
      {
        id: 1,
        name: 'Prefix Sums Basics',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-immutable/',
        questions: [
          {
            question: 'What is the main use of prefix sums in array problems?',
            options: [
              'To quickly update array values',
              'To answer range sum queries in O(1) time after O(n) preprocessing',
              'To sort the array',
              'To find the maximum element'
            ],
            correctAnswer: 1,
            explanation: 'Prefix sums allow us to answer range sum queries in O(1) time after O(n) preprocessing.'
          },
          {
            question: 'What is the formula to compute the sum of a subarray [l, r] using prefix sums?',
            options: [
              'PrefixSum[r] - PrefixSum[l]',
              'PrefixSum[r] - PrefixSum[l-1]',
              'PrefixSum[l] - PrefixSum[r-1]',
              'PrefixSum[r+1] - PrefixSum[l]'
            ],
            correctAnswer: 1,
            explanation: 'The sum of [l, r] is PrefixSum[r] - PrefixSum[l-1].'
          },
          {
            question: 'Why do we sometimes initialize prefix sums with a 0 at the start?',
            options: [
              'To make the array longer',
              'So that PrefixSum[-1] = 0 and sum([0, x]) = PrefixSum[x] - PrefixSum[-1]',
              'To avoid negative numbers',
              'To make updates easier'
            ],
            correctAnswer: 1,
            explanation: 'Initializing with 0 allows sum([0, x]) = PrefixSum[x] - 0 = PrefixSum[x].'
          },
          {
            question: 'Can prefix sums be used if the array is frequently updated?',
            options: [
              'Yes, efficiently',
              'No, because prefix sums require O(n) to update',
              'Only for multiplication',
              'Only for XOR'
            ],
            correctAnswer: 1,
            explanation: 'Prefix sums are not efficient for updates; use segment trees for that.'
          },
          {
            question: 'What is the identity element for prefix sum, multiplication, and XOR?',
            options: [
              '0, 0, 0',
              '1, 1, 1',
              '0, 1, 0',
              '1, 0, 1'
            ],
            correctAnswer: 2,
            explanation: 'Addition: 0, Multiplication: 1, XOR: 0.'
          },
          {
            question: 'Which of the following is NOT a valid prefix operation?',
            options: [
              'Prefix sum',
              'Prefix multiplication',
              'Prefix XOR',
              'Prefix division'
            ],
            correctAnswer: 3,
            explanation: 'Division is not associative and does not have a simple inverse for prefix queries.'
          }
        ]
      },
      {
        id: 2,
        name: 'Range Sum Query - Immutable',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-immutable/',
        questions: [
          {
            question: 'What is the time complexity of sumRange(left, right) after prefix sum preprocessing?',
            options: ['O(n)', 'O(log n)', 'O(1)', 'O(n^2)'],
            correctAnswer: 2,
            explanation: 'After O(n) preprocessing, each query is O(1) using prefix sums.'
          },
          {
            question: 'What is the main limitation of prefix sums for range queries?',
            options: [
              'They cannot handle negative numbers',
              'They cannot handle updates efficiently',
              'They use too much memory',
              'They are slower than brute force'
            ],
            correctAnswer: 1,
            explanation: 'Prefix sums are not efficient for updates; segment trees are used for that.'
          },
          {
            question: 'What built-in Python function can be used to compute prefix sums?',
            options: ['accumulate', 'sum', 'reduce', 'map'],
            correctAnswer: 0,
            explanation: 'itertools.accumulate computes prefix sums.'
          }
        ]
      },
      {
        id: 3,
        name: '2D Prefix Sums',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-2d-immutable/',
        questions: [
          {
            question: 'What is the formula for the sum of a submatrix using 2D prefix sums?',
            options: [
              'PS[ii][jj] - PS[i][j]',
              'PS[ii][jj] - PS[ii][j-1] - PS[i-1][jj] + PS[i-1][j-1]',
              'PS[ii][jj] + PS[i][j]',
              'PS[ii][jj] - PS[i][j-1] + PS[i-1][jj]'
            ],
            correctAnswer: 1,
            explanation: 'The sum of a submatrix is PS[ii][jj] - PS[ii][j-1] - PS[i-1][jj] + PS[i-1][j-1].'
          },
          {
            question: 'What is the time complexity to answer a sumRegion query after 2D prefix sum preprocessing?',
            options: ['O(n^2)', 'O(n)', 'O(1)', 'O(log n)'],
            correctAnswer: 2,
            explanation: 'After O(mn) preprocessing, each query is O(1).' 
          }
        ]
      },
      {
        id: 4,
        name: 'Prefix Sums Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-size-subarray-sum/',
        questions: [
          {
            question: 'How can prefix sums and binary search be combined to solve Minimum Size Subarray Sum?',
            options: [
              'Use prefix sums to get subarray sums and binary search to find the leftmost index',
              'Use only prefix sums',
              'Use only binary search',
              'Use a stack'
            ],
            correctAnswer: 0,
            explanation: 'Prefix sums allow O(1) subarray sum queries, and binary search finds the leftmost valid index.'
          },
          {
            question: 'Why do we use bisect_right instead of bisect_left in this context?',
            options: [
              'To handle duplicates correctly',
              'To find the smallest index',
              'To find the largest index',
              'To avoid infinite loops'
            ],
            correctAnswer: 0,
            explanation: 'bisect_right ensures we handle duplicate prefix sums correctly.'
          }
        ]
      },
      {
        id: 5,
        name: 'Prefix Multiplication and XOR',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/product-of-array-except-self/',
        questions: [
          {
            question: 'How can prefix multiplication be used to solve Product of Array Except Self?',
            options: [
              'By dividing the total product by nums[i]',
              'By using prefix and suffix products and multiplying them for each index',
              'By using prefix sums',
              'By sorting the array first'
            ],
            correctAnswer: 1,
            explanation: 'Prefix and suffix products allow us to compute the product except self without division.'
          },
          {
            question: 'What is the formula to recover the original array from a prefix XOR array?',
            options: [
              'arr[i] = pref[i] ^ pref[i-1]',
              'arr[i] = pref[i] + pref[i-1]',
              'arr[i] = pref[i] - pref[i-1]',
              'arr[i] = pref[i] * pref[i-1]'
            ],
            correctAnswer: 0,
            explanation: 'arr[i] = pref[i] ^ pref[i-1] because XOR is its own inverse.'
          }
        ]
      },
      {
        id: 6,
        name: 'Prefix Sums in Simulation',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-time-to-visit-all-houses/',
        questions: [
          {
            question: 'How are prefix sums used in the Minimum Time to Visit All Houses problem?',
            options: [
              'To quickly compute the total distance in either direction',
              'To sort the houses',
              'To find the minimum house number',
              'To count the number of queries'
            ],
            correctAnswer: 0,
            explanation: 'Prefix sums allow O(1) computation of total distance in either direction.'
          },
          {
            question: 'Why do we need both prefix sums for forward and backward directions?',
            options: [
              'To handle circular paths efficiently',
              'To avoid negative numbers',
              'To make the code shorter',
              'To handle updates'
            ],
            correctAnswer: 0,
            explanation: 'Both are needed to compute the shortest path in either direction on a circle.'
          }
        ]
      }
    ]
  },
  'parenthesis': {
    title: 'Parenthesis',
    problems: [
      {
        id: 1,
        name: 'Valid Parentheses',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/valid-parentheses/',
        questions: [
          {
            question: 'What data structure is most commonly used to check for balanced parentheses?',
            options: ['Queue', 'Stack', 'Hash Map', 'Array'],
            correctAnswer: 1,
            explanation: 'A stack is used because it is last-in-first-out, matching the order of opening and closing parentheses.'
          },
          {
            question: 'What are the two main cases for unbalanced parentheses?',
            options: [
              'Extra closing parenthesis or extra opening parenthesis',
              'Extra digits or extra operators',
              'Mismatched brackets or missing operators',
              'Extra spaces or missing numbers'
            ],
            correctAnswer: 0,
            explanation: 'Case 1: Extra closing parenthesis (no matching open). Case 2: Extra opening parenthesis (not closed).'
          },
          {
            question: 'What is the time and space complexity of the stack-based solution for Valid Parentheses?',
            options: ['O(n) time, O(n) space', 'O(n^2) time, O(1) space', 'O(1) time, O(n) space', 'O(n) time, O(1) space'],
            correctAnswer: 0,
            explanation: 'We may need to store all opening parentheses in the stack.'
          },
          {
            question: 'How can you optimize the solution if there is only one type of parenthesis?',
            options: [
              'Use a queue',
              'Use a counter instead of a stack',
              'Use a hash map',
              'Use recursion'
            ],
            correctAnswer: 1,
            explanation: 'A counter is sufficient if only one type of parenthesis is present.'
          }
        ]
      },
      {
        id: 2,
        name: 'Generate Parentheses',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/generate-parentheses/',
        questions: [
          {
            question: 'What algorithmic technique is used to generate all valid combinations of n pairs of parentheses?',
            options: ['Dynamic Programming', 'Backtracking', 'Greedy', 'Sorting'],
            correctAnswer: 1,
            explanation: 'Backtracking is used to explore all valid combinations.'
          },
          {
            question: 'How do you ensure that a generated parenthesis string is valid during backtracking?',
            options: [
              'By checking the string at the end',
              'By ensuring the number of opening parentheses is never less than closing at any point',
              'By sorting the string',
              'By using a hash map'
            ],
            correctAnswer: 1,
            explanation: 'At any point, the number of closing parentheses should not exceed the number of opening.'
          }
        ]
      },
      {
        id: 3,
        name: 'Valid Parenthesis String (with *)',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/valid-parenthesis-string/',
        questions: [
          {
            question: 'What does the * character represent in the Valid Parenthesis String problem?',
            options: [
              'A wildcard that can be (, ), or empty',
              'A closing parenthesis',
              'An opening parenthesis',
              'A digit'
            ],
            correctAnswer: 0,
            explanation: 'The * can be treated as (, ), or empty.'
          },
          {
            question: 'What is the greedy approach to solve Valid Parenthesis String with *?',
            options: [
              'Try all possibilities recursively',
              'Check from left to right and right to left, treating * as ( and ) respectively',
              'Use a stack for all *',
              'Use dynamic programming only'
            ],
            correctAnswer: 1,
            explanation: 'Greedily treat * as ( in one pass and as ) in the other.'
          }
        ]
      },
      {
        id: 4,
        name: 'Check if a Parentheses String Can Be Valid (locked)',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/check-if-a-parentheses-string-can-be-valid/',
        questions: [
          {
            question: 'What does the locked string represent in the Check if a Parentheses String Can Be Valid problem?',
            options: [
              'Whether a parenthesis can be changed',
              'Whether a parenthesis is open',
              'Whether a parenthesis is closed',
              'Whether a parenthesis is a digit'
            ],
            correctAnswer: 0,
            explanation: 'locked[i] = 1 means s[i] cannot be changed; locked[i] = 0 means it can.'
          },
          {
            question: 'What is the key insight for solving the locked parentheses problem?',
            options: [
              'Use the same greedy two-pass approach as with *',
              'Use a stack for all locked positions',
              'Sort the string',
              'Use a hash map'
            ],
            correctAnswer: 0,
            explanation: 'The greedy two-pass approach works, treating unlocked positions as wildcards.'
          }
        ]
      },
      {
        id: 5,
        name: 'Minimum Swaps to Make the String Balanced',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-number-of-swaps-to-make-the-string-balanced/',
        questions: [
          {
            question: 'What is the optimal way to balance a string of [ and ] with equal numbers of each?',
            options: [
              'Count the number of unmatched [ and return ceil(count/2)',
              'Use a stack for all brackets',
              'Sort the string',
              'Use dynamic programming'
            ],
            correctAnswer: 0,
            explanation: 'Count unmatched [ after removing all balanced pairs; answer is ceil(count/2).'
          }
        ]
      },
      {
        id: 6,
        name: 'Minimum Add/Remove to Make Parentheses Valid',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-add-to-make-parentheses-valid/',
        questions: [
          {
            question: 'How do you compute the minimum number of insertions to make a parentheses string valid?',
            options: [
              'Count unmatched ( and ) using a counter',
              'Use a stack for all characters',
              'Sort the string',
              'Use dynamic programming'
            ],
            correctAnswer: 0,
            explanation: 'Count unmatched ( and ) as you iterate; sum gives the answer.'
          }
        ]
      },
      {
        id: 7,
        name: 'Minimum Remove to Make Valid Parentheses',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-remove-to-make-valid-parentheses/',
        questions: [
          {
            question: 'How do you compute the minimum number of removals to make a parentheses string valid?',
            options: [
              'Track indices of unmatched ( and ) and remove them',
              'Use a stack for all characters',
              'Sort the string',
              'Use dynamic programming'
            ],
            correctAnswer: 0,
            explanation: 'Track indices of unmatched ( and ) and remove them from the string.'
          }
        ]
      },
      {
        id: 8,
        name: 'Parenthesis in Calculators and Parsing',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/basic-calculator/',
        questions: [
          {
            question: 'How are stacks used in parsing expressions with parentheses (e.g., calculators)?',
            options: [
              'To match opening and closing parentheses and evaluate sub-expressions',
              'To sort the numbers',
              'To store all operators',
              'To keep track of digits only'
            ],
            correctAnswer: 0,
            explanation: 'Stacks help match parentheses and evaluate expressions inside them recursively.'
          }
        ]
      },
      {
        id: 9,
        name: 'Different Ways to Add Parentheses',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/different-ways-to-add-parentheses/',
        questions: [
          {
            question: 'What is the key insight for computing all possible results from adding parentheses in an expression?',
            options: [
              'Divide and conquer with memoization (DP)',
              'Sort the string',
              'Use a stack for all operators',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Divide and conquer with memoization is used to compute all possible results.'
          }
        ]
      }
    ]
  },
  'segment-tree': {
    title: 'Segment Tree',
    problems: [
      {
        id: 1,
        name: 'Segment Tree Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-mutable/',
        questions: [
          {
            question: 'What is the main advantage of a segment tree over prefix sums?',
            options: [
              'Faster queries',
              'Handles both range queries and updates in O(log n) time',
              'Uses less memory',
              'Easier to implement'
            ],
            correctAnswer: 1,
            explanation: 'Segment trees can handle both range queries and updates in O(log n) time, while prefix sums cannot handle updates efficiently.'
          },
          {
            question: 'What does each node in a segment tree represent?',
            options: [
              'A single element',
              'A segment (range) of the array',
              'A prefix sum',
              'A suffix sum'
            ],
            correctAnswer: 1,
            explanation: 'Each node represents a segment (range) of the array and stores information about that segment.'
          },
          {
            question: 'What are the three main operations supported by a segment tree?',
            options: [
              'Build, Query, Update',
              'Sort, Search, Delete',
              'Insert, Delete, Search',
              'Push, Pop, Top'
            ],
            correctAnswer: 0,
            explanation: 'Build (O(n)), Query (O(log n)), and Update (O(log n)).'
          },
          {
            question: 'What is the time complexity for a range query or point update in a segment tree?',
            options: ['O(1)', 'O(log n)', 'O(n)', 'O(n log n)'],
            correctAnswer: 1,
            explanation: 'Both range queries and point updates are O(log n) in a segment tree.'
          },
          {
            question: 'What is the difference between a segment tree and a Fenwick tree (BIT)?',
            options: [
              'Segment tree is faster',
              'Fenwick tree supports more operations',
              'They have the same asymptotic complexity, but segment trees are more flexible',
              'Fenwick tree is always preferred'
            ],
            correctAnswer: 2,
            explanation: 'Both have O(log n) query/update, but segment trees can support more types of range operations.'
          }
        ]
      },
      {
        id: 2,
        name: 'Segment Tree Operations',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-mutable/',
        questions: [
          {
            question: 'What are the three cases to check in a segment tree query?',
            options: [
              'Inside, Outside, Partial Overlap',
              'Left, Right, Middle',
              'Leaf, Root, Internal',
              'None of the above'
            ],
            correctAnswer: 0,
            explanation: 'You must check if the current segment is fully inside, fully outside, or partially overlaps the query range.'
          },
          {
            question: 'What does the combine() function do in a segment tree?',
            options: [
              'Combines the values of the left and right children to update the parent',
              'Deletes a node',
              'Sorts the array',
              'Finds the minimum value'
            ],
            correctAnswer: 0,
            explanation: 'combine() merges the results of the left and right children to update the parent node.'
          },
          {
            question: 'What is lazy propagation in segment trees used for?',
            options: [
              'Faster point updates',
              'Efficient range updates',
              'Sorting the array',
              'Reducing memory usage'
            ],
            correctAnswer: 1,
            explanation: 'Lazy propagation allows efficient range updates in O(log n) time.'
          },
          {
            question: 'What are some common operations that can be implemented with a segment tree?',
            options: [
              'Sum, Min, Max, XOR, Product, GCD, LCM',
              'Sort, Search, Delete',
              'Push, Pop, Top',
              'Insert, Delete, Search'
            ],
            correctAnswer: 0,
            explanation: 'Segment trees can be used for any associative operation that can be built up recursively.'
          }
        ]
      },
      {
        id: 3,
        name: 'Segment Tree Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-mutable/',
        questions: [
          {
            question: 'How do you use a segment tree to solve Range Sum Query - Mutable?',
            options: [
              'Build a segment tree for sum, use update for point updates, and query for range sums',
              'Use prefix sums',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Build a sum segment tree, use update for point updates, and query for range sums.'
          },
          {
            question: 'How does coordinate compression help in segment tree problems?',
            options: [
              'It reduces the range of indices, making the segment tree smaller and more efficient',
              'It sorts the array',
              'It increases the range of values',
              'It is not useful'
            ],
            correctAnswer: 0,
            explanation: 'Coordinate compression maps large values to a smaller range, reducing memory usage.'
          },
          {
            question: 'What is the key insight for using a segment tree in "Count of Smaller Numbers After Self"?',
            options: [
              'Use a frequency segment tree with coordinate compression, scan right to left, and query for counts of smaller elements',
              'Use a stack',
              'Use prefix sums',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Build a frequency segment tree, scan right to left, and query for counts of smaller elements.'
          },
          {
            question: 'What is the main difference between a sum, min, and max segment tree?',
            options: [
              'The combine() function and the neutral value for queries',
              'The tree structure',
              'The update function',
              'The query function'
            ],
            correctAnswer: 0,
            explanation: 'Only the combine() and the neutral value (0, inf, -inf) differ.'
          }
        ]
      },
      {
        id: 4,
        name: 'Segment Tree vs SortedList',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/count-of-smaller-numbers-after-self/',
        questions: [
          {
            question: 'When might you use a SortedList instead of a segment tree?',
            options: [
              'When the API is simpler and the array is not too large',
              'When you need O(1) updates',
              'When you need to sort the array',
              'When you need to delete elements'
            ],
            correctAnswer: 0,
            explanation: 'SortedList is often simpler to use for problems like "Count of Smaller Numbers After Self".'
          },
          {
            question: 'What is the main similarity between segment trees and SortedList?',
            options: [
              'Both can answer range queries and support updates',
              'Both are binary trees',
              'Both use O(n log n) space',
              'Both are used for sorting'
            ],
            correctAnswer: 0,
            explanation: 'Both can answer range queries and support updates, but with different APIs.'
          }
        ]
      },
      {
        id: 5,
        name: 'Advanced Segment Tree Concepts',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/132-pattern/',
        questions: [
          {
            question: 'What is lazy propagation and when is it necessary?',
            options: [
              'A technique for efficient range updates, necessary when you need to update a range of values in O(log n) time',
              'A way to build the tree faster',
              'A way to reduce memory usage',
              'A way to sort the array'
            ],
            correctAnswer: 0,
            explanation: 'Lazy propagation is used for efficient range updates.'
          },
          {
            question: 'What are the three key questions to ask when designing a segment tree?',
            options: [
              'What operation, what do the indices represent, what do the values represent',
              'What is the array size, what is the time complexity, what is the space complexity',
              'What is the query function, what is the update function, what is the combine function',
              'What is the root value, what is the leaf value, what is the height of the tree'
            ],
            correctAnswer: 0,
            explanation: 'You must know what operation, what the indices represent, and what the values represent.'
          },
          {
            question: 'How can segment trees be used to solve the 132 pattern problem?',
            options: [
              'By using min/max segment trees and coordinate compression to efficiently check for the pattern',
              'By sorting the array',
              'By using a stack',
              'By using prefix sums'
            ],
            correctAnswer: 0,
            explanation: 'Min/max segment trees and coordinate compression allow efficient pattern checking.'
          }
        ]
      }
    ]
  },
  'monotonic-stack': {
    title: 'Monotonic Stack',
    problems: [
      {
        id: 1,
        name: 'Monotonic Stack Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/next-greater-element-i/',
        questions: [
          {
            question: 'What is the main use case for a monotonic stack?',
            options: [
              'Finding the next greater or smaller element in O(1) per element',
              'Sorting an array',
              'Binary search',
              'Prefix sum queries'
            ],
            correctAnswer: 0,
            explanation: 'Monotonic stacks are used to efficiently find the next greater/smaller element for each index.'
          },
          {
            question: 'How many variations of the monotonic stack template are there?',
            options: [
              '2', '4', '6', '8'
            ],
            correctAnswer: 3,
            explanation: 'There are 8 variations: next/previous, greater/smaller, and strict/non-strict.'
          },
          {
            question: 'What are the three main variables you change in the monotonic stack template?',
            options: [
              'X, Y, Z',
              'A, B, C',
              'i, j, k',
              'n, m, l'
            ],
            correctAnswer: 0,
            explanation: 'X (init value), Y (iteration direction), Z (comparison operator).' 
          },
          {
            question: 'What is the time and space complexity of the monotonic stack algorithm?',
            options: [
              'O(n) time, O(n) space', 'O(n^2) time, O(1) space', 'O(log n) time, O(n) space', 'O(n) time, O(1) space'
            ],
            correctAnswer: 0,
            explanation: 'Each element is pushed and popped at most once.'
          }
        ]
      },
      {
        id: 2,
        name: 'Monotonic Stack Template',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/next-greater-element-i/',
        questions: [
          {
            question: 'What is the master template for monotonic stack problems?',
            options: [
              'A for loop with a stack and a comparison operator',
              'A while loop with a queue',
              'A recursive function',
              'A binary search'
            ],
            correctAnswer: 0,
            explanation: 'The master template uses a for loop, a stack, and a comparison operator to determine when to pop.'
          },
          {
            question: 'How do you adapt the template for next/previous, greater/smaller, and strict/non-strict?',
            options: [
              'Change the initial value, iteration direction, and comparison operator',
              'Change the array size',
              'Change the stack to a queue',
              'Change the return value'
            ],
            correctAnswer: 0,
            explanation: 'You change X (init value), Y (direction), and Z (comparison).' 
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Monotonic Stack Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/daily-temperatures/',
        questions: [
          {
            question: 'How do you solve the Daily Temperatures problem using a monotonic stack?',
            options: [
              'Use a stack to keep indices of decreasing temperatures and update the result when a warmer day is found',
              'Sort the array',
              'Use prefix sums',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Maintain a stack of indices with decreasing temperatures; when a warmer day is found, update the result.'
          },
          {
            question: 'How do you handle circular arrays in next greater element problems?',
            options: [
              'Double the array and use modulo for indices',
              'Sort the array',
              'Use a queue',
              'Use binary search'
            ],
            correctAnswer: 0,
            explanation: 'Double the array and use modulo to simulate circular behavior.'
          },
          {
            question: 'What is the key insight for Largest Rectangle in Histogram?',
            options: [
              'Use previous and next lower elements to determine the width for each bar',
              'Sort the array',
              'Use prefix sums',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'The width is determined by the distance between previous and next lower elements.'
          }
        ]
      },
      {
        id: 4,
        name: 'Contribution Technique & Subarray Problems',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/sum-of-subarray-minimums/',
        questions: [
          {
            question: 'How do you use monotonic stacks to solve sum of subarray minimums?',
            options: [
              'Find previous less and next less or equal for each element, then count subarrays where it is the minimum',
              'Sort the array',
              'Use prefix sums',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Use monotonic stacks to find the range where each element is the minimum.'
          },
          {
            question: 'What is the contribution technique?',
            options: [
              'Count how many subarrays each element is the min/max for, and sum their contributions',
              'Sort the array',
              'Use prefix sums',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'The contribution technique counts subarrays for which each element is the min/max.'
          }
        ]
      },
      {
        id: 5,
        name: 'Monotonic Deque & Sliding Window',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/sliding-window-maximum/',
        questions: [
          {
            question: 'How do you use a monotonic deque to solve the sliding window maximum problem?',
            options: [
              'Maintain a deque of indices with decreasing values, pop from both ends as needed',
              'Sort the array',
              'Use prefix sums',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'A monotonic deque allows O(1) access to the max in the current window.'
          },
          {
            question: 'Why does the monotonic deque approach work for sliding window maximum?',
            options: [
              'Because the deque always contains the indices of the largest elements in the window',
              'Because it sorts the window',
              'Because it uses prefix sums',
              'Because it uses a queue'
            ],
            correctAnswer: 0,
            explanation: 'The deque maintains the indices of the largest elements in the window.'
          }
        ]
      }
    ]
  },
  'heap': {
    title: 'Heap',
    problems: [
      {
        id: 1,
        name: 'Heap Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/kth-largest-element-in-an-array/',
        questions: [
          {
            question: 'What is a heap?',
            options: [
              'A complete binary tree that allows fast access to min/max elements',
              'A sorted array',
              'A hash map',
              'A linked list'
            ],
            correctAnswer: 0,
            explanation: 'A heap is a complete binary tree that allows O(log n) insertions, deletions, and O(1) access to the top element.'
          },
          {
            question: 'What is the difference between a min heap and a max heap?',
            options: [
              'Min heap stores the minimum at the top, max heap stores the maximum',
              'Min heap is sorted, max heap is not',
              'Max heap is faster',
              'There is no difference'
            ],
            correctAnswer: 0,
            explanation: 'Min heap stores the minimum at the top, max heap stores the maximum.'
          },
          {
            question: 'What is the time complexity of heapify, heappush, and heappop?',
            options: [
              'O(n), O(log n), O(log n)',
              'O(log n), O(n), O(n)',
              'O(n log n), O(1), O(1)',
              'O(1), O(1), O(1)'
            ],
            correctAnswer: 0,
            explanation: 'heapify is O(n), heappush and heappop are O(log n).' 
          },
          {
            question: 'How do you implement a max heap in Python using the heapq module?',
            options: [
              'Negate all values before pushing to the heap',
              'Use a different module',
              'Sort the array',
              'Use a stack'
            ],
            correctAnswer: 0,
            explanation: 'Negating values turns the min heap into a max heap.'
          }
        ]
      },
      {
        id: 2,
        name: 'Top K / Bottom K Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/kth-largest-element-in-an-array/',
        questions: [
          {
            question: 'What are three ways to solve top K problems?',
            options: [
              'Sort, Heap of size K, Quickselect',
              'Sort, Stack, Queue',
              'Binary search, Hash map, Stack',
              'Prefix sum, Segment tree, Stack'
            ],
            correctAnswer: 0,
            explanation: 'Sort (O(n log n)), heap of size K (O(n log K)), and quickselect (O(n) average).' 
          },
          {
            question: 'When do you use a min heap vs a max heap for top K/bottom K problems?',
            options: [
              'Min heap for top K, max heap for bottom K',
              'Max heap for top K, min heap for bottom K',
              'Always use min heap',
              'Always use max heap'
            ],
            correctAnswer: 0,
            explanation: 'Min heap for top K (remove smallest), max heap for bottom K (remove largest).' 
          },
          {
            question: 'What is the time complexity of finding the Kth largest element using a heap of size K?',
            options: [
              'O(n log K)', 'O(n)', 'O(n log n)', 'O(log n)'],
            correctAnswer: 0,
            explanation: 'Each insertion/removal is O(log K), and we do it n times.'
          }
        ]
      },
      {
        id: 3,
        name: 'Heap Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/k-closest-points-to-origin/',
        questions: [
          {
            question: 'How do you use a heap to find the K closest points to the origin?',
            options: [
              'Use a max heap of size K with negative squared distances',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Use a max heap of size K, push negative squared distances to simulate max heap.'
          },
          {
            question: 'How do you use a heap to solve the Top K Frequent Words problem?',
            options: [
              'Use a heap of tuples (-frequency, word) and pop K times',
              'Sort the array',
              'Use a stack',
              'Use a queue'
            ],
            correctAnswer: 0,
            explanation: 'Heap of (-frequency, word) allows us to get the top K frequent words.'
          }
        ]
      },
      {
        id: 4,
        name: 'Median of Data Stream (2 Heaps)',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/find-median-from-data-stream/',
        questions: [
          {
            question: 'How do you use two heaps to maintain the median of a data stream?',
            options: [
              'Use a max heap for the left half and a min heap for the right half, balance sizes after each insertion',
              'Sort the array after each insertion',
              'Use a single heap',
              'Use a stack'
            ],
            correctAnswer: 0,
            explanation: 'Max heap for left, min heap for right, balance after each insertion.'
          },
          {
            question: 'What are the two invariants to maintain in the two-heap median approach?',
            options: [
              'Size and value invariants',
              'Sum and product invariants',
              'Order and frequency invariants',
              'Heap and stack invariants'
            ],
            correctAnswer: 0,
            explanation: 'Size: left >= right and left <= right+1; Value: top of left <= top of right.'
          }
        ]
      },
      {
        id: 5,
        name: 'Heap vs SortedList',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/find-median-from-data-stream/',
        questions: [
          {
            question: 'What is a key advantage of SortedList over heap?',
            options: [
              'O(log n) deletion from anywhere',
              'Faster insertions',
              'Less memory usage',
              'Easier to implement'
            ],
            correctAnswer: 0,
            explanation: 'SortedList allows O(log n) deletion from anywhere, while heaps only allow O(log n) pop from the top.'
          },
          {
            question: 'When should you use a heap vs a SortedList?',
            options: [
              'Heap for fast top access and streaming, SortedList for fast arbitrary deletion and order statistics',
              'Always use heap',
              'Always use SortedList',
              'Heap for sorting, SortedList for searching'
            ],
            correctAnswer: 0,
            explanation: 'Heap is best for streaming top elements, SortedList for order statistics and deletions.'
          }
        ]
      }
    ]
  },
  'sliding-window': {
    title: 'Sliding Window',
    problems: [
      {
        id: 1,
        name: 'Basics & Intuition',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/sliding-window/',
        questions: [
          {
            question: 'What is the sliding window technique used for?',
            options: [
              'Finding optimal subarrays/substrings in O(n) time',
              'Sorting arrays',
              'Finding shortest paths in graphs',
              'Balancing parentheses'
            ],
            correctAnswer: 0,
            explanation: 'Sliding window is used to find optimal subarrays/substrings efficiently.'
          },
          {
            question: 'What is the brute force time complexity for checking all subarrays?',
            options: ['O(n)', 'O(n^2)', 'O(log n)', 'O(1)'],
            correctAnswer: 1,
            explanation: 'Brute force checks all {n choose 2} subarrays, which is O(n^2).'
          },
          {
            question: 'What are the two main types of sliding window templates?',
            options: ['Max and Min', 'Prefix and Suffix', 'Stack and Queue', 'DFS and BFS'],
            correctAnswer: 0,
            explanation: 'Sliding window templates are Max (longest/maximum) and Min (shortest/minimum).'
          },
          {
            question: 'What is the key monotonic property required for sliding window?',
            options: [
              'is_valid(s[l,r]) is monotonic for all fixed l',
              'Array must be sorted',
              'All elements must be unique',
              'Window size must be fixed'
            ],
            correctAnswer: 0,
            explanation: 'The is_valid function must be monotonic for sliding window to work.'
          }
        ]
      },
      {
        id: 2,
        name: 'Max/Min Template',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/sliding-window/',
        questions: [
          {
            question: 'In the Max template, when do you update the result?',
            options: [
              'Outside the inner while loop, when the window is valid',
              'Inside the inner while loop',
              'Before moving the right pointer',
              'After the for loop ends'
            ],
            correctAnswer: 0,
            explanation: 'For Max template, update result outside the while loop when the window is valid.'
          },
          {
            question: 'In the Min template, when do you update the result?',
            options: [
              'Inside the inner while loop, when the window is valid',
              'Outside the inner while loop',
              'Before moving the right pointer',
              'After the for loop ends'
            ],
            correctAnswer: 0,
            explanation: 'For Min template, update result inside the while loop when the window is valid.'
          },
          {
            question: 'What is a common data structure for the window in sliding window problems?',
            options: ['Counter/frequency map', 'Stack', 'Heap', 'Trie'],
            correctAnswer: 0,
            explanation: 'A frequency map (Counter) is commonly used to track window contents.'
          },
          {
            question: 'What is the time complexity of most sliding window solutions?',
            options: ['O(n)', 'O(n^2)', 'O(log n)', 'O(1)'],
            correctAnswer: 0,
            explanation: 'Sliding window optimizes to O(n) time for most problems.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/longest-substring-without-repeating-characters/',
        questions: [
          {
            question: 'What is the sliding window solution for Longest Substring Without Repeating Characters?',
            options: [
              'Use a frequency map to track characters and move left pointer when a repeat is found',
              'Sort the string',
              'Use a stack',
              'Use a heap'
            ],
            correctAnswer: 0,
            explanation: 'Track character counts and move left pointer to remove repeats.'
          },
          {
            question: 'How do you solve Fruit Into Baskets (904) with sliding window?',
            options: [
              'Track the count of each fruit type in the window and move left pointer when more than 2 types',
              'Sort the array',
              'Use a stack',
              'Use a heap'
            ],
            correctAnswer: 0,
            explanation: 'Maintain a frequency map and ensure at most 2 types in the window.'
          },
          {
            question: 'What is the sliding window solution for Max Consecutive Ones III (1004)?',
            options: [
              'Count the number of 0s in the window and move left pointer when more than k',
              'Sort the array',
              'Use a stack',
              'Use a heap'
            ],
            correctAnswer: 0,
            explanation: 'Count 0s and move left pointer when more than k 0s in the window.'
          },
          {
            question: 'How do you solve Minimum Size Subarray Sum (209) with sliding window?',
            options: [
              'Expand right pointer to increase sum, then move left pointer to minimize window while sum >= target',
              'Sort the array',
              'Use a stack',
              'Use a heap'
            ],
            correctAnswer: 0,
            explanation: 'Expand right, then contract left to find minimal window with sum >= target.'
          }
        ]
      },
      {
        id: 4,
        name: 'Fixed Window & Variants',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/sliding-window-maximum/',
        questions: [
          {
            question: 'What is the main difference between fixed and dynamic sliding window?',
            options: [
              'Fixed window has constant size, dynamic window can grow/shrink',
              'Fixed window is always valid',
              'Dynamic window is always valid',
              'Fixed window uses a stack'
            ],
            correctAnswer: 0,
            explanation: 'Fixed window has constant size, dynamic window can grow or shrink.'
          },
          {
            question: 'How do you solve Sliding Window Maximum (239) with a monotonic deque?',
            options: [
              'Maintain a deque of indices with decreasing values, pop from both ends as needed',
              'Sort the window',
              'Use a heap',
              'Use a stack'
            ],
            correctAnswer: 0,
            explanation: 'A monotonic deque allows O(1) access to the max in the current window.'
          },
          {
            question: 'How do you count good substrings of size 3 with distinct characters?',
            options: [
              'Use a frequency map and check window of size 3 for unique characters',
              'Sort the string',
              'Use a stack',
              'Use a heap'
            ],
            correctAnswer: 0,
            explanation: 'Check each window of size 3 for unique characters using a frequency map.'
          },
          {
            question: 'What is the time complexity of fixed window sliding window problems?',
            options: ['O(n)', 'O(n^2)', 'O(log n)', 'O(1)'],
            correctAnswer: 0,
            explanation: 'Each window is processed in O(1), for O(n) total.'
          }
        ]
      },
      {
        id: 5,
        name: 'Advanced & Variants',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/sliding-window/',
        questions: [
          {
            question: 'How do you solve problems that require "exactly K" using sliding window?',
            options: [
              'Use the formula: at most K - at most (K-1)',
              'Sort the array',
              'Use a stack',
              'Use a heap'
            ],
            correctAnswer: 0,
            explanation: 'Exactly K = at most K - at most (K-1) is a common trick.'
          },
          {
            question: 'How can binary search be combined with sliding window?',
            options: [
              'Binary search on window size, check validity with sliding window',
              'Sort the array',
              'Use a stack',
              'Use a heap'
            ],
            correctAnswer: 0,
            explanation: 'Binary search can fix window size, sliding window checks validity.'
          },
          {
            question: 'What is a 2D sliding window?',
            options: [
              'A window that slides over a matrix/submatrix',
              'A window that slides over a string',
              'A window that slides over a stack',
              'A window that slides over a heap'
            ],
            correctAnswer: 0,
            explanation: '2D sliding window slides over submatrices in a grid.'
          },
          {
            question: 'What is the main requirement for using sliding window in a problem?',
            options: [
              'The is_valid function must be monotonic',
              'Array must be sorted',
              'All elements must be unique',
              'Window size must be fixed'
            ],
            correctAnswer: 0,
            explanation: 'Monotonicity of is_valid is required for sliding window.'
          }
        ]
      }
    ]
  },
  'tree': {
    title: 'Trees',
    problems: [
      {
        id: 1,
        name: 'Basics & Structure',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        questions: [
          {
            question: 'What is a tree data structure?',
            options: [
              'A hierarchical structure with nodes and edges',
              'A linear structure like an array',
              'A circular structure',
              'A hash map'
            ],
            correctAnswer: 0,
            explanation: 'A tree is a hierarchical structure with nodes connected by edges.'
          },
          {
            question: 'What is the root of a tree?',
            options: [
              'The topmost node',
              'A node with no children',
              'A node with two parents',
              'A leaf node'
            ],
            correctAnswer: 0,
            explanation: 'The root is the topmost node in a tree.'
          },
          {
            question: 'What is a leaf node?',
            options: [
              'A node with no children',
              'A node with two children',
              'A node with no parent',
              'A node with a single child'
            ],
            correctAnswer: 0,
            explanation: 'A leaf node has no children.'
          },
          {
            question: 'What is the difference between a binary tree and an n-ary tree?',
            options: [
              'Binary tree nodes have at most 2 children, n-ary can have up to n',
              'Binary trees are always balanced',
              'n-ary trees are always sorted',
              'Binary trees have only one child per node'
            ],
            correctAnswer: 0,
            explanation: 'Binary trees have at most 2 children per node; n-ary trees can have up to n.'
          }
        ]
      },
      {
        id: 2,
        name: 'Traversals',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        questions: [
          {
            question: 'What are the three main types of depth-first traversals?',
            options: [
              'Preorder, Inorder, Postorder',
              'BFS, DFS, Dijkstra',
              'Level order, Zigzag, Spiral',
              'Stack, Queue, Recursion'
            ],
            correctAnswer: 0,
            explanation: 'The three main DFS traversals are Preorder, Inorder, and Postorder.'
          },
          {
            question: 'What is the order of nodes visited in inorder traversal?',
            options: [
              'Left, Root, Right',
              'Root, Left, Right',
              'Left, Right, Root',
              'Right, Root, Left'
            ],
            correctAnswer: 0,
            explanation: 'Inorder visits Left, then Root, then Right.'
          },
          {
            question: 'Which traversal is used to get sorted values from a BST?',
            options: [
              'Inorder',
              'Preorder',
              'Postorder',
              'Level order'
            ],
            correctAnswer: 0,
            explanation: 'Inorder traversal of a BST yields sorted values.'
          },
          {
            question: 'What is the base case for most tree recursion?',
            options: [
              'If the node is null',
              'If the node is a leaf',
              'If the node has two children',
              'If the node is the root'
            ],
            correctAnswer: 0,
            explanation: 'The base case is usually when the node is null.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        questions: [
          {
            question: 'How do you invert a binary tree?',
            options: [
              'Swap left and right children recursively',
              'Sort the tree',
              'Remove all leaves',
              'Add a new root'
            ],
            correctAnswer: 0,
            explanation: 'Invert by recursively swapping left and right children.'
          },
          {
            question: 'How do you check if two trees are the same?',
            options: [
              'Recursively compare values and structure',
              'Check only the root values',
              'Compare the number of nodes',
              'Check if both are balanced'
            ],
            correctAnswer: 0,
            explanation: 'Recursively compare both value and structure.'
          },
          {
            question: 'How do you check if a tree is symmetric?',
            options: [
              'Compare left and right subtrees as mirrors',
              'Check if all values are equal',
              'Check if the tree is balanced',
              'Check if the root is a leaf'
            ],
            correctAnswer: 0,
            explanation: 'Symmetry means left and right subtrees are mirrors.'
          },
          {
            question: 'How do you compute the maximum depth of a binary tree?',
            options: [
              'Recursively take 1 + max depth of left and right',
              'Count the number of leaves',
              'Sum all node values',
              'Count the number of edges'
            ],
            correctAnswer: 0,
            explanation: 'Max depth is 1 + the max of left and right subtree depths.'
          },
          {
            question: 'How do you check if a root-to-leaf path sums to a target?',
            options: [
              'Recursively subtract node values from target and check at leaves',
              'Sum all node values',
              'Check only the root value',
              'Use BFS only'
            ],
            correctAnswer: 0,
            explanation: 'Subtract node values as you traverse, check at leaves.'
          }
        ]
      },
      {
        id: 4,
        name: 'BST Properties & Validation',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/validate-binary-search-tree/',
        questions: [
          {
            question: 'What is the key property of a binary search tree (BST)?',
            options: [
              'Left subtree < root < right subtree',
              'All nodes have two children',
              'All values are unique',
              'Tree is always balanced'
            ],
            correctAnswer: 0,
            explanation: 'BST property: left subtree values < root < right subtree values.'
          },
          {
            question: 'How do you validate a BST?',
            options: [
              'Pass down min and max constraints during recursion',
              'Check only the root',
              'Sort the tree',
              'Count the number of nodes'
            ],
            correctAnswer: 0,
            explanation: 'Pass down constraints to ensure all values are within bounds.'
          },
          {
            question: 'What is the time complexity of validating a BST?',
            options: ['O(n)', 'O(log n)', 'O(n^2)', 'O(1)'],
            correctAnswer: 0,
            explanation: 'Each node is visited once.'
          },
          {
            question: 'What is the result of inorder traversal of a BST?',
            options: [
              'A sorted array of values',
              'A reversed array',
              'A random order',
              'A level order traversal'
            ],
            correctAnswer: 0,
            explanation: 'Inorder traversal of a BST yields sorted values.'
          }
        ]
      },
      {
        id: 5,
        name: 'Advanced & State Tracking',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        questions: [
          {
            question: 'How do you find the lowest common ancestor (LCA) of two nodes in a binary tree?',
            options: [
              'Recursively find the node where both nodes split',
              'Sort the tree',
              'Check only the root',
              'Use BFS only'
            ],
            correctAnswer: 0,
            explanation: 'LCA is found where the two nodes split in the tree.'
          },
          {
            question: 'How do you compute the sum of all root-to-leaf numbers?',
            options: [
              'Track the path value as you traverse and sum at leaves',
              'Sum all node values',
              'Count the number of leaves',
              'Use BFS only'
            ],
            correctAnswer: 0,
            explanation: 'Track the path value and sum at each leaf.'
          },
          {
            question: 'What is the key pattern in tree problems involving state tracking?',
            options: [
              'Combine results from subtrees and use global/local state',
              'Sort the tree',
              'Check only the root',
              'Use BFS only'
            ],
            correctAnswer: 0,
            explanation: 'Combine results from subtrees and use state variables.'
          },
          {
            question: 'What is the time complexity of most tree traversals?',
            options: ['O(n)', 'O(log n)', 'O(n^2)', 'O(1)'],
            correctAnswer: 0,
            explanation: 'Each node is visited once.'
          }
        ]
      }
    ]
  },
  'arrays-and-hashing': {
    title: 'Arrays and Hashing',
    problems: [
      {
        id: 1,
        name: 'Basics',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/array/',
        questions: [
          {
            question: 'What is the time complexity of adding and looking up an element in a set or dict in Python?',
            options: ['O(1)', 'O(n)', 'O(log n)', 'O(n^2)'],
            correctAnswer: 0,
            explanation: 'Both set and dict provide average O(1) time for add, remove, and lookup.'
          },
          {
            question: 'What is the difference between a set and a dict?',
            options: [
              'A set stores only unique keys, a dict stores key-value pairs',
              'A set is ordered, a dict is not',
              'A dict can only store integers',
              'A set can have duplicate values'
            ],
            correctAnswer: 0,
            explanation: 'A set stores unique keys, a dict stores key-value pairs.'
          },
          {
            question: 'What does defaultdict do that a regular dict does not?',
            options: [
              'Returns a default value for missing keys',
              'Sorts the keys',
              'Stores only integers',
              'Throws a KeyError for missing keys'
            ],
            correctAnswer: 0,
            explanation: 'defaultdict returns a default value for missing keys.'
          },
          {
            question: 'What does Counter([1,2,3,1]) return?',
            options: [
              '{1: 2, 2: 1, 3: 1}',
              '{1: 1, 2: 1, 3: 1}',
              '[1, 2, 3, 1]',
              '2'
            ],
            correctAnswer: 0,
            explanation: 'Counter counts the frequency of each element.'
          }
        ]
      },
      {
        id: 2,
        name: 'Hash Maps & Sets',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/hash-table/',
        questions: [
          {
            question: 'How do you solve the Two Sum problem efficiently?',
            options: [
              'Use a hash map to store seen values and their indices',
              'Sort the array and use two pointers',
              'Brute force all pairs',
              'Use a stack'
            ],
            correctAnswer: 0,
            explanation: 'A hash map allows O(1) lookup for complements.'
          },
          {
            question: 'What is the main advantage of using a set to check for duplicates?',
            options: [
              'O(1) lookup for existence',
              'O(n) lookup for existence',
              'It sorts the array',
              'It removes all elements'
            ],
            correctAnswer: 0,
            explanation: 'Sets provide O(1) lookup for existence.'
          },
          {
            question: 'How does defaultdict(int) behave when accessing a missing key?',
            options: [
              'Returns 0',
              'Throws KeyError',
              'Returns None',
              'Returns -1'
            ],
            correctAnswer: 0,
            explanation: 'defaultdict(int) returns 0 for missing keys.'
          },
          {
            question: 'What is the output of len(set([1,2,2,3]))?',
            options: ['3', '4', '2', '1'],
            correctAnswer: 0,
            explanation: 'Set removes duplicates, so the length is 3.'
          }
        ]
      },
      {
        id: 3,
        name: 'Prefix Sums & Subarrays',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/prefix-sum/',
        questions: [
          {
            question: 'How do you count the number of subarrays with sum equal to k?',
            options: [
              'Use a hash map to store prefix sums and their frequencies',
              'Sort the array',
              'Use a stack',
              'Brute force all subarrays'
            ],
            correctAnswer: 0,
            explanation: 'Hash map of prefix sums enables O(n) solution.'
          },
          {
            question: 'What is the time complexity of the optimal Subarray Sum Equals K solution?',
            options: ['O(n)', 'O(n^2)', 'O(log n)', 'O(1)'],
            correctAnswer: 0,
            explanation: 'Each element is processed once.'
          },
          {
            question: 'How do you check if a subarray sum is divisible by k?',
            options: [
              'Track prefix sums modulo k in a hash map',
              'Sort the array',
              'Use a stack',
              'Brute force all subarrays'
            ],
            correctAnswer: 0,
            explanation: 'Store prefix sums modulo k to find divisible subarrays.'
          },
          {
            question: 'How do you count subarrays with an odd sum?',
            options: [
              'Track even/odd prefix sums and their counts',
              'Sort the array',
              'Use a stack',
              'Brute force all subarrays'
            ],
            correctAnswer: 0,
            explanation: 'Track even/odd prefix sums to count odd subarrays.'
          }
        ]
      },
      {
        id: 4,
        name: 'Anagrams',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/hash-table/',
        questions: [
          {
            question: 'How do you check if two strings are anagrams?',
            options: [
              'Compare sorted strings or use Counter',
              'Check if they are the same length',
              'Check if they have the same first letter',
              'Use a stack'
            ],
            correctAnswer: 0,
            explanation: 'Sorting or using Counter can check for anagrams.'
          },
          {
            question: 'How do you group anagrams in a list of strings?',
            options: [
              'Group by sorted string as key in a hash map',
              'Sort the list',
              'Use a stack',
              'Group by string length'
            ],
            correctAnswer: 0,
            explanation: 'Group by sorted string as key in a hash map.'
          },
          {
            question: 'What is the time complexity of grouping anagrams?',
            options: ['O(nm)', 'O(n^2)', 'O(m^2)', 'O(1)'],
            correctAnswer: 0,
            explanation: 'n = number of strings, m = average string length.'
          },
          {
            question: 'What is an anagram?',
            options: [
              'A word formed by rearranging the letters of another',
              'A palindrome',
              'A substring',
              'A hash value'
            ],
            correctAnswer: 0,
            explanation: 'An anagram is a rearrangement of letters.'
          }
        ]
      },
      {
        id: 5,
        name: 'Duplicates & Cycles',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/hash-table/',
        questions: [
          {
            question: 'How do you check if an array contains duplicates?',
            options: [
              'Compare length of set and array',
              'Sort the array',
              'Use a stack',
              'Brute force all pairs'
            ],
            correctAnswer: 0,
            explanation: 'Set removes duplicates, so compare lengths.'
          },
          {
            question: 'How do you check for nearby duplicates within k distance?',
            options: [
              'Use a hash map to track last seen indices',
              'Sort the array',
              'Use a stack',
              'Brute force all pairs'
            ],
            correctAnswer: 0,
            explanation: 'Hash map tracks last seen indices for O(1) lookup.'
          },
          {
            question: 'How do you find the minimum number of swaps to sort an array by digit sum?',
            options: [
              'Decompose into cycles and sum (cycle length - 1)',
              'Sort and count inversions',
              'Use a stack',
              'Brute force all swaps'
            ],
            correctAnswer: 0,
            explanation: 'Decompose into cycles and sum (cycle length - 1).' 
          },
          {
            question: 'What is the main use of sets in graph traversals?',
            options: [
              'Track visited nodes',
              'Sort the graph',
              'Store edge weights',
              'Track node degrees'
            ],
            correctAnswer: 0,
            explanation: 'Sets are used to track visited nodes.'
          }
        ]
      }
    ]
  },
  'graph': {
    title: 'Graphs',
    problems: [
      {
        id: 1,
        name: 'Basics & Types',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/graph/',
        questions: [
          {
            question: 'What is a graph in computer science?',
            options: [
              'A set of vertices and edges connecting them',
              'A sorted array',
              'A binary tree',
              'A hash map'
            ],
            correctAnswer: 0,
            explanation: 'A graph is a set of vertices (nodes) and edges (connections).'
          },
          {
            question: 'What is the difference between a directed and undirected graph?',
            options: [
              'Directed edges have a direction, undirected do not',
              'Undirected graphs have weights',
              'Directed graphs are always cyclic',
              'Undirected graphs are always trees'
            ],
            correctAnswer: 0,
            explanation: 'Directed edges go one way; undirected go both ways.'
          },
          {
            question: 'What is a weighted graph?',
            options: [
              'Edges have associated values (weights)',
              'All nodes have the same value',
              'It is always a tree',
              'It is always cyclic'
            ],
            correctAnswer: 0,
            explanation: 'Weighted graphs have values on their edges.'
          },
          {
            question: 'What is an adjacency list?',
            options: [
              'A mapping from each node to its neighbors',
              'A sorted list of nodes',
              'A list of all edges',
              'A hash map of node values'
            ],
            correctAnswer: 0,
            explanation: 'Adjacency list maps each node to its neighbors.'
          }
        ]
      },
      {
        id: 2,
        name: 'Traversals (BFS/DFS)',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/breadth-first-search/',
        questions: [
          {
            question: 'What does BFS stand for?',
            options: [
              'Breadth First Search',
              'Binary First Search',
              'Backtracking First Search',
              'Best First Search'
            ],
            correctAnswer: 0,
            explanation: 'BFS = Breadth First Search.'
          },
          {
            question: 'What is the main property of BFS in unweighted graphs?',
            options: [
              'Finds shortest path from source to all nodes',
              'Finds all cycles',
              'Finds minimum spanning tree',
              'Finds all connected components'
            ],
            correctAnswer: 0,
            explanation: 'BFS finds shortest path in unweighted graphs.'
          },
          {
            question: 'What is the main property of DFS?',
            options: [
              'Recursively explores as far as possible along each branch',
              'Finds shortest path',
              'Always iterative',
              'Only works on trees'
            ],
            correctAnswer: 0,
            explanation: 'DFS explores as far as possible recursively.'
          },
          {
            question: 'What is the time complexity of BFS/DFS on a graph with n nodes and m edges?',
            options: ['O(n + m)', 'O(n^2)', 'O(m^2)', 'O(1)'],
            correctAnswer: 0,
            explanation: 'Each node and edge is visited at most once.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/graph/',
        questions: [
          {
            question: 'How do you count the number of islands in a grid?',
            options: [
              'Use BFS or DFS to mark all connected land cells',
              'Sort the grid',
              'Use a stack',
              'Brute force all pairs'
            ],
            correctAnswer: 0,
            explanation: 'BFS/DFS can mark all cells in an island.'
          },
          {
            question: 'How do you clone a graph?',
            options: [
              'Use BFS/DFS and a map from old to new nodes',
              'Sort the nodes',
              'Use a stack',
              'Brute force all pairs'
            ],
            correctAnswer: 0,
            explanation: 'Map old nodes to new nodes during traversal.'
          },
          {
            question: 'How do you find the number of connected components in an undirected graph?',
            options: [
              'Run DFS/BFS from each unvisited node',
              'Sort the nodes',
              'Use a stack',
              'Brute force all pairs'
            ],
            correctAnswer: 0,
            explanation: 'Each DFS/BFS from an unvisited node finds a new component.'
          },
          {
            question: 'How do you check if all rooms can be visited given keys in each room?',
            options: [
              'DFS from room 0, check if all rooms are visited',
              'Sort the rooms',
              'Use a stack',
              'Brute force all pairs'
            ],
            correctAnswer: 0,
            explanation: 'DFS from room 0, track visited rooms.'
          }
        ]
      },
      {
        id: 4,
        name: 'Dijkstra & Shortest Path',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/shortest-path/',
        questions: [
          {
            question: 'What is Dijkstra algorithm used for?',
            options: [
              'Finding shortest paths in non-negative weighted graphs',
              'Finding minimum spanning trees',
              'Finding all cycles',
              'Finding connected components'
            ],
            correctAnswer: 0,
            explanation: 'Dijkstra finds shortest paths in non-negative weighted graphs.'
          },
          {
            question: 'What data structure is used in Dijkstra algorithm?',
            options: [
              'Min heap (priority queue)',
              'Stack',
              'Hash map',
              'Set'
            ],
            correctAnswer: 0,
            explanation: 'A min heap is used to always process the closest node next.'
          },
          {
            question: 'What is the time complexity of Dijkstra algorithm with a binary heap?',
            options: ['O((n + m) log n)', 'O(n^2)', 'O(m^2)', 'O(1)'],
            correctAnswer: 0,
            explanation: 'Each node/edge can be added to the heap, each heap op is O(log n).'
          },
          {
            question: 'What is 0-1 BFS?',
            options: [
              'A BFS optimization for graphs with edge weights 0 or 1',
              'A DFS variant',
              'A sorting algorithm',
              'A type of spanning tree'
            ],
            correctAnswer: 0,
            explanation: '0-1 BFS uses a deque for 0/1 edge weights.'
          }
        ]
      },
      {
        id: 5,
        name: 'Topological Sort & Advanced',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/topological-sort/',
        questions: [
          {
            question: 'What is topological sort used for?',
            options: [
              'Ordering nodes in a DAG so all edges go forward',
              'Finding shortest paths',
              'Finding minimum spanning trees',
              'Finding all cycles'
            ],
            correctAnswer: 0,
            explanation: 'Topological sort orders nodes in a DAG.'
          },
          {
            question: 'What is Kahn algorithm?',
            options: [
              'A BFS-based algorithm for topological sort',
              'A DFS-based algorithm for minimum spanning tree',
              'A sorting algorithm',
              'A type of heap'
            ],
            correctAnswer: 0,
            explanation: 'Kahn algorithm is a BFS-based topological sort.'
          },
          {
            question: 'How do you detect a cycle in a directed graph?',
            options: [
              'If topological sort is not possible (not all nodes processed)',
              'If BFS finds a node twice',
              'If all nodes have indegree 0',
              'If the graph is connected'
            ],
            correctAnswer: 0,
            explanation: 'If topological sort does not process all nodes, there is a cycle.'
          },
          {
            question: 'What is a strongly connected component (SCC)?',
            options: [
              'A maximal set of nodes where each node is reachable from every other',
              'A minimum spanning tree',
              'A shortest path',
              'A set of isolated nodes'
            ],
            correctAnswer: 0,
            explanation: 'SCC: every node can reach every other node in the component.'
          }
        ]
      }
    ]
  },
  'dynamic-programming': {
    title: 'Dynamic Programming',
    problems: [
      {
        id: 1,
        name: 'Basics & Intuition',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        questions: [
          {
            question: 'What is the main idea behind dynamic programming (DP)?',
            options: [
              'Breaking problems into overlapping subproblems and storing results to avoid recomputation',
              'Sorting arrays',
              'Using recursion without memoization',
              'Greedy selection of local optimums'
            ],
            correctAnswer: 0,
            explanation: 'DP solves problems by breaking them into overlapping subproblems and storing their results.'
          },
          {
            question: 'What is the time complexity of naive recursive Fibonacci?',
            options: ['O(2^n)', 'O(n)', 'O(log n)', 'O(n^2)'],
            correctAnswer: 0,
            explanation: 'Naive recursion recomputes subproblems, leading to exponential time.'
          },
          {
            question: 'What is memoization in DP?',
            options: [
              'Caching results of subproblems to avoid recomputation',
              'Sorting the input',
              'Using a stack for recursion',
              'Iterating backwards only'
            ],
            correctAnswer: 0,
            explanation: 'Memoization stores results of subproblems for reuse.'
          },
          {
            question: 'Which of the following is NOT a typical DP optimization?',
            options: [
              'Memoization', 'Tabulation', 'Greedy choice', 'State reduction'
            ],
            correctAnswer: 2,
            explanation: 'Greedy choice is not a DP optimization.'
          }
        ]
      },
      {
        id: 2,
        name: 'Recurrence & Memoization',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/fibonacci-number/',
        questions: [
          {
            question: 'What is the recurrence relation for Fibonacci numbers?',
            options: [
              'F(n) = F(n-1) + F(n-2)',
              'F(n) = n * F(n-1)',
              'F(n) = F(n-1) - F(n-2)',
              'F(n) = 2 * F(n-1)'
            ],
            correctAnswer: 0,
            explanation: 'Fibonacci is defined as F(n) = F(n-1) + F(n-2) with base cases F(0)=0, F(1)=1.'
          },
          {
            question: 'What is the main benefit of memoization in recursive DP?',
            options: [
              'Reduces time complexity by avoiding repeated work',
              'Reduces space complexity',
              'Increases recursion depth',
              'Makes code harder to debug'
            ],
            correctAnswer: 0,
            explanation: 'Memoization avoids recomputation, reducing time complexity.'
          },
          {
            question: 'Which decorator in Python can be used for memoization?',
            options: ['@cache', '@memo', '@lru_cache', '@dataclass'],
            correctAnswer: 2,
            explanation: '@lru_cache is a standard memoization decorator.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        questions: [
          {
            question: 'Which DP problem asks for the number of ways to climb a staircase with 1 or 2 steps at a time?',
            options: [
              'Climbing Stairs', 'House Robber', 'Coin Change', 'Edit Distance'
            ],
            correctAnswer: 0,
            explanation: 'Climbing Stairs is a classic DP problem.'
          },
          {
            question: 'What is the optimal substructure property?',
            options: [
              'A problem can be solved using solutions to its subproblems',
              'A problem can only be solved greedily',
              'A problem cannot be broken into subproblems',
              'A problem must be solved iteratively'
            ],
            correctAnswer: 0,
            explanation: 'Optimal substructure means the optimal solution can be constructed from optimal solutions of subproblems.'
          },
          {
            question: 'Which of the following is a classic DP problem involving partitioning an array into two subsets with equal sum?',
            options: [
              'Partition Equal Subset Sum', 'Longest Increasing Subsequence', 'Unique Paths', 'Decode Ways'
            ],
            correctAnswer: 0,
            explanation: 'Partition Equal Subset Sum is a classic DP subset problem.'
          },
          {
            question: 'What is the time complexity of the O(n^2) solution for Longest Increasing Subsequence (LIS)?',
            options: ['O(n^2)', 'O(n)', 'O(2^n)', 'O(n log n)'],
            correctAnswer: 0,
            explanation: 'The DP solution for LIS is O(n^2).' 
          }
        ]
      },
      {
        id: 4,
        name: 'State & Dimensions',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        questions: [
          {
            question: 'What does the "state" in DP refer to?',
            options: [
              'A unique configuration of parameters that define a subproblem',
              'A variable in a for loop',
              'A function argument',
              'A constant value'
            ],
            correctAnswer: 0,
            explanation: 'State is the set of parameters that uniquely define a subproblem.'
          },
          {
            question: 'What is the time complexity of a DP with state dp(i, j) where both i and j go from 0 to n?',
            options: ['O(n^2)', 'O(n)', 'O(2^n)', 'O(n log n)'],
            correctAnswer: 0,
            explanation: 'There are n^2 possible states.'
          },
          {
            question: 'Which of the following is an example of a 2D DP problem?',
            options: [
              'Edit Distance', 'Fibonacci', 'Climbing Stairs', 'House Robber'
            ],
            correctAnswer: 0,
            explanation: 'Edit Distance uses a 2D DP table.'
          }
        ]
      },
      {
        id: 5,
        name: 'Advanced & Variants',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        questions: [
          {
            question: 'What is bitmask DP used for?',
            options: [
              'Efficiently representing subsets/states using bits',
              'Sorting arrays',
              'Finding shortest paths in graphs',
              'Balancing parentheses'
            ],
            correctAnswer: 0,
            explanation: 'Bitmask DP is used to represent subsets or combinations efficiently.'
          },
          {
            question: 'Which of the following is a classic bitmask DP problem?',
            options: [
              'Partition to K Equal Sum Subsets', 'Climbing Stairs', 'Coin Change', 'Unique Paths'
            ],
            correctAnswer: 0,
            explanation: 'Partition to K Equal Sum Subsets is a classic bitmask DP problem.'
          },
          {
            question: 'What is the main challenge in digit DP problems?',
            options: [
              'Handling constraints digit by digit',
              'Sorting digits',
              'Using recursion only',
              'Avoiding memoization'
            ],
            correctAnswer: 0,
            explanation: 'Digit DP requires careful handling of constraints for each digit.'
          },
          {
            question: 'Which of the following is a tree DP problem?',
            options: [
              'Unique Binary Search Trees', 'Coin Change', 'Decode Ways', 'Edit Distance'
            ],
            correctAnswer: 0,
            explanation: 'Unique Binary Search Trees is a classic tree DP problem.'
          }
        ]
      },
      {
        id: 6,
        name: 'Interval, Greedy, and Binary Search DP',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        questions: [
          {
            question: 'What is interval DP and when is it typically used?',
            options: [
              'When the subproblems are defined over intervals (i, j), such as in Matrix Chain Multiplication or palindrome partitioning',
              'When the problem can be solved greedily',
              'When the state is a single index',
              'When the problem is on a tree structure'
            ],
            correctAnswer: 0,
            explanation: 'Interval DP is used when subproblems are defined over intervals, e.g., dp(i, j) for i <= j.'
          },
          {
            question: 'How can binary search be combined with DP?',
            options: [
              'To optimize the search for a valid DP state or answer, such as in LIS O(n log n) or min/max value problems',
              'To sort the input',
              'To reduce space complexity',
              'To avoid recursion'
            ],
            correctAnswer: 0,
            explanation: 'Binary search is often used with DP to optimize the search for a valid answer or state.'
          },
          {
            question: 'Which of the following is a classic greedy + DP problem?',
            options: [
              'Job Scheduling (Weighted Interval Scheduling)', 'Climbing Stairs', 'Edit Distance', 'Unique Paths'
            ],
            correctAnswer: 0,
            explanation: 'Weighted Interval Scheduling is solved with DP and greedy sorting by end time.'
          },
          {
            question: 'What is the DP state for the Maximum Profit in Job Scheduling problem?',
            options: [
              'dp(i): max profit for jobs[0..i]',
              'dp(i, j): max profit for jobs[i..j]',
              'dp(i): max profit for jobs[i..n-1]',
              'dp(mask): max profit for a subset of jobs'
            ],
            correctAnswer: 2,
            explanation: 'dp(i) is the max profit for jobs[i..n-1], solved by sorting and binary search.'
          }
        ]
      },
      {
        id: 7,
        name: 'Tree DP',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        questions: [
          {
            question: 'What is tree DP?',
            options: [
              'DP where subproblems are defined on subtrees of a tree',
              'DP on intervals',
              'DP with bitmasks',
              'DP on arrays only'
            ],
            correctAnswer: 0,
            explanation: 'Tree DP is used when the problem is defined on a tree and subproblems are on subtrees.'
          },
          {
            question: 'What is a common technique to avoid cycles in tree DP?',
            options: [
              'Passing the parent node as a parameter and skipping it in recursion',
              'Using a stack',
              'Sorting the nodes',
              'Using a queue'
            ],
            correctAnswer: 0,
            explanation: 'Passing the parent node prevents revisiting the parent and causing cycles.'
          },
          {
            question: 'Which of the following is a classic tree DP problem?',
            options: [
              'House Robber III', 'Coin Change', 'Edit Distance', 'Longest Increasing Subsequence'
            ],
            correctAnswer: 0,
            explanation: 'House Robber III is a classic tree DP problem.'
          },
          {
            question: 'What is the typical DP state for tree DP?',
            options: [
              'dp(node): optimal value for the subtree rooted at node',
              'dp(i, j): optimal value for interval [i, j]',
              'dp(mask): optimal value for a subset',
              'dp(i): optimal value for prefix [0, i]'
            ],
            correctAnswer: 0,
            explanation: 'Tree DP usually defines dp(node) as the optimal value for the subtree rooted at node.'
          }
        ]
      }
    ]
  },
};

function ProblemRow({ problem, onStartQuiz, topicId, isCompleted, onToggle }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { 
            bgcolor: 'rgba(81, 104, 255, 0.05)'
          },
          cursor: 'pointer'
        }}
      >
        <TableCell>
          <Checkbox
            checked={isCompleted}
            onChange={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            sx={{
              color: '#5168ff',
              '&.Mui-checked': {
                color: '#5168ff',
              },
            }}
          />
        </TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell onClick={() => setOpen(!open)}>
          {problem.name}
        </TableCell>
        <TableCell>
          <Link
            component={isInternalRoute(internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)) ? RouterLink : 'a'}
            to={isInternalRoute(internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)) ? internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name) : undefined}
            href={isInternalRoute(internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)) ? undefined : internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)}
            target={isInternalRoute(internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)) ? undefined : '_blank'}
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#5168ff'
            }}
          >
            Solve <LaunchIcon fontSize="small" />
          </Link>
        </TableCell>
        <TableCell>
          <Typography
            sx={{
              color: problem.difficulty === 'Easy' ? '#15905f' :
                     problem.difficulty === 'Medium' ? '#b7791f' : '#c24150'
            }}
          >
            {problem.difficulty}
          </Typography>
        </TableCell>
        <TableCell>{problem.questions.length} questions</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Button
                variant="contained"
                onClick={() => onStartQuiz(problem, problem.questions)}
                sx={{
                  mb: 2,
                  background: '#0a0d14',
                  '&:hover': {
                    background: '#05070c',
                  }
                }}
              >
                Start Quiz ({problem.questions.length} questions)
              </Button>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                Quiz Questions:
              </Typography>
              {problem.questions.map((q, index) => (
                <Typography 
                  key={index} 
                  sx={{ 
                    color: 'text.primary',
                    mb: 1,
                    pl: 2,
                    borderLeft: '2px solid #5168ff'
                  }}
                >
                  {q.question}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}



function Quizzes() {
  const [completedSections, setCompletedSections] = useState(() => {
    const saved = localStorage.getItem('completedQuizSections');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('completedQuizSections', JSON.stringify(completedSections));
  }, [completedSections]);

  // Calculate progress statistics
  const progressStats = React.useMemo(() => {
    const allProblems = Object.entries(quizzesByTopic).flatMap(([topicId, topic]) => 
      topic.problems.map(problem => ({
        ...problem,
        topicId // Add topicId to each problem
      }))
    );
    const total = allProblems.length;
    const completed = Object.values(completedSections).filter(Boolean).length;
    
    // By difficulty
    const difficultyStats = allProblems.reduce((acc, problem) => {
      if (!acc[problem.difficulty]) {
        acc[problem.difficulty] = {
          total: 0,
          completed: 0
        };
      }
      acc[problem.difficulty].total++;
      if (completedSections[`${problem.topicId}-${problem.id}`]) {
        acc[problem.difficulty].completed++;
      }
      return acc;
    }, {});

    // By topic
    const topicStats = Object.entries(quizzesByTopic).reduce((acc, [topicId, topic]) => {
      acc[topicId] = {
        title: topic.title,
        total: topic.problems.length,
        completed: topic.problems.filter(problem => 
          completedSections[`${topicId}-${problem.id}`]
        ).length
      };
      return acc;
    }, {});

    return {
      total,
      completed,
      difficultyStats,
      topicStats
    };
  }, [completedSections]);

  const handleSectionToggle = (topicId, problemId) => {
    const key = `${topicId}-${problemId}`;
    setCompletedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [isQuizMode, setIsQuizMode] = useState(false);
  const [topicProgressOpen, setTopicProgressOpen] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [previousTopic, setPreviousTopic] = useState(null);
  const { user } = useAuth();
  const { hasActiveSubscription, loading: subLoading } = useSubscriptionStatus();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleTopicSelect = (topicId) => {
    setSelectedTopic(topicId);
    setIsQuizMode(false);
  };

  const handleStartQuiz = (problem, questions) => {
    setCurrentQuiz({
      title: problem.name,
      topic: quizzesByTopic[selectedTopic].title,
      questions: questions.map(q => ({
        ...q,
        problemName: problem.name,
        problemUrl: problem.leetcodeUrl
      }))
    });
    setIsQuizMode(true);
    resetQuiz(questions.length);
  };

  const resetQuiz = (numQuestions = 0) => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setQuizCompleted(false);
    setQuizAnswers(Array(numQuestions).fill(null));
  };

  const currentQuestion = currentQuiz ? currentQuiz.questions[currentQuestionIndex] : null;
  const progress = currentQuiz ? ((currentQuestionIndex + 1) / currentQuiz.questions.length) * 100 : 0;

  const handleAnswerSelect = (event) => {
    setSelectedAnswer(Number(event.target.value));
    setShowExplanation(false);
  };

  const handleSubmit = () => {
    if (selectedAnswer === null) return;

    if (!showExplanation) {
      setShowExplanation(true);
      const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
      if (isCorrect) {
        setScore(score + 1);
      }
      setQuizAnswers(prev => {
        const updated = [...prev];
        updated[currentQuestionIndex] = isCorrect;
        return updated;
      });
    } else {
      if (currentQuestionIndex + 1 < currentQuiz.questions.length) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setSelectedAnswer(null);
        setShowExplanation(false);
      } else {
        setQuizCompleted(true);
      }
    }
  };

  const createRandomQuiz = () => {
    setPreviousTopic(selectedTopic);
    // Get all questions from all topics and their problems
    const allQuestions = Object.values(quizzesByTopic).flatMap(topic => 
      topic.problems.flatMap(problem => 
        problem.questions.map(q => ({
          ...q,
          topic: topic.title,
          problemName: problem.name,
          problemUrl: problem.leetcodeUrl
        }))
      )
    );
    
    // Randomly select 5 questions
    const randomQuestions = [];
    const questionsCopy = [...allQuestions];
    for (let i = 0; i < 5 && questionsCopy.length > 0; i++) {
      const randomIndex = Math.floor(Math.random() * questionsCopy.length);
      randomQuestions.push(questionsCopy.splice(randomIndex, 1)[0]);
    }

    setCurrentQuiz({
      title: 'Random Quiz',
      questions: randomQuestions
    });
    setSelectedTopic('random');
    setIsQuizMode(true);
    resetQuiz();
  };

  const handleStudyAll = (topicId) => {
    // Get all questions from all problems in the topic
    const allQuestions = quizzesByTopic[topicId].problems.flatMap(problem => 
      problem.questions.map(q => ({
        ...q,
        problemName: problem.name,
        problemUrl: problem.leetcodeUrl
      }))
    );

    setCurrentQuiz({
      title: `${quizzesByTopic[topicId].title} - All Questions`,
      topic: quizzesByTopic[topicId].title,
      questions: allQuestions
    });
    setIsQuizMode(true);
    resetQuiz();
  };

  if (quizCompleted) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
        <Paper 
          sx={{ 
            p: 4,
            bgcolor: 'rgba(81, 104, 255, 0.05)',
            border: '1px solid rgba(81, 104, 255, 0.1)',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ color: '#5168ff' }}>
            Quiz Completed!
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            Your score: {score} / {currentQuiz.questions.length}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => {
              if (currentQuiz) {
                resetQuiz(currentQuiz.questions.length);
              }
            }}
            sx={{ mt: 2, mr: 2 }}
          >
            Retry Quiz
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setIsQuizMode(false);
              setQuizCompleted(false);
              setCurrentQuiz(null);
              // seems to work even if we comment these next 4 lines out?
              if (selectedTopic === 'random' && previousTopic) {
                setSelectedTopic(previousTopic);
                setPreviousTopic(null);
              }
            }}
            sx={{ mt: 2 }}
          >
            Back
          </Button>
          {/* Mark Quiz as Completed button: only if 100% and not already completed, and not random/study all */}
          {score === currentQuiz.questions.length &&
           currentQuiz.title &&
           selectedTopic &&
           selectedTopic !== 'random' &&
           !currentQuiz.title.includes('All Questions') &&
           quizzesByTopic[selectedTopic]?.problems &&
           (() => {
             // Find the problem id for this quiz
             const problem = quizzesByTopic[selectedTopic].problems.find(
               p => p.name === currentQuiz.title
             );
             if (!problem) return null;
             const key = `${selectedTopic}-${problem.id}`;
             if (completedSections[key]) return null;
             return (
               <Button
                 variant="contained"
                 color="success"
                 sx={{ mt: 2, mr: 2 }}
                 onClick={() => {
                   setCompletedSections(prev => ({
                     ...prev,
                     [key]: true
                   }));
                   setQuizCompleted(false);
                   setIsQuizMode(false);
                   setCurrentQuiz(null);
                 }}
               >
                 Mark Quiz as Completed
               </Button>
             );
           })()
          }
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{
      py: { xs: 5, md: 7 },
      minHeight: 'calc(100vh - 120px)',
      '& .MuiPaper-root, & .MuiCard-root': {
        background: (theme) => `${theme.palette.background.paper} !important`,
        backgroundImage: 'none !important',
        border: (theme) => `1px solid ${theme.palette.divider} !important`,
        boxShadow: (theme) => theme.palette.mode === 'dark'
          ? '0 24px 80px rgba(0,0,0,0.24) !important'
          : '0 24px 80px rgba(82,107,170,0.12) !important',
        backdropFilter: 'blur(18px)',
      },
      '& .MuiButton-contained': {
        background: (theme) => `${theme.palette.primary.main} !important`,
        color: (theme) => `${theme.palette.primary.contrastText} !important`,
      },
    }}>
      <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 5 }}>
        <Typography
          variant="overline"
          sx={{
            display: 'inline-flex',
            px: 2,
            py: 0.6,
            mb: 2,
            borderRadius: 999,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 800,
          }}
        >
          Quizzes
        </Typography>
        <Typography variant="h2" sx={{ color: 'text.primary', fontSize: { xs: '2.8rem', md: '4.8rem' }, lineHeight: 0.95, fontWeight: 850 }}>
          Test the pattern before it fades.
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.08rem', lineHeight: 1.7, mt: 2 }}>
          Pick a topic, answer focused questions, and reinforce the reasoning behind each interview pattern.
        </Typography>
      </Box>
      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Box sx={{ mb: 3 }}>
            <Tooltip
              title={
                subLoading
                  ? 'Checking subscription...'
                  : !user
                  ? 'Sign in to access Random Quiz (Premium)'
                  : !hasActiveSubscription
                  ? 'Random Quiz is for Premium users only'
                  : ''
              }
              disableHoverListener={user && hasActiveSubscription}
              disableFocusListener={user && hasActiveSubscription}
              disableTouchListener={user && hasActiveSubscription}
            >
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ShuffleIcon />}
                  onClick={createRandomQuiz}
                  sx={{
                    p: 2,
                  }}
                  disabled={subLoading || !user || !hasActiveSubscription}
                >
                  Random Quiz (5)
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Sidebar 
            mode="quizzes" 
            onTopicSelect={handleTopicSelect}
            selectedTopic={selectedTopic}
            completedChapters={completedSections}
            quizzesByTopic={quizzesByTopic}
          />
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          {isQuizMode ? (
            // Quiz UI
            <Box>
              <Container maxWidth="lg" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsQuizMode(false);
                      setCurrentQuiz(null);
                      if (selectedTopic === 'random' && previousTopic) {
                        setSelectedTopic(previousTopic);
                        setPreviousTopic(null);
                      }
                    }}
                  >
                    Back
                  </Button>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#5168ff',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    From: {currentQuiz.topic} → {' '}
                    <Link
                      component={isInternalRoute(internalProblemPathFromLeetcodeUrl(currentQuestion.problemUrl, currentQuestion.problemName)) ? RouterLink : 'a'}
                      to={isInternalRoute(internalProblemPathFromLeetcodeUrl(currentQuestion.problemUrl, currentQuestion.problemName)) ? internalProblemPathFromLeetcodeUrl(currentQuestion.problemUrl, currentQuestion.problemName) : undefined}
                      href={isInternalRoute(internalProblemPathFromLeetcodeUrl(currentQuestion.problemUrl, currentQuestion.problemName)) ? undefined : internalProblemPathFromLeetcodeUrl(currentQuestion.problemUrl, currentQuestion.problemName)}
                      target={isInternalRoute(internalProblemPathFromLeetcodeUrl(currentQuestion.problemUrl, currentQuestion.problemName)) ? undefined : '_blank'}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: '#5168ff'
                      }}
                    >
                      {currentQuestion.problemName} <LaunchIcon fontSize="small" />
                    </Link>
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center',
                    background: '#0a0d14',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 4
                  }}
                >
                  {currentQuiz.title} Quiz
                </Typography>

                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progress} 
                      sx={{ 
                        mb: 4, 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: 'rgba(81, 104, 255, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#5168ff'
                        }
                      }} 
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Paper 
                      sx={{ 
                        p: 4,
                        bgcolor: 'rgba(81, 104, 255, 0.05)',
                        border: '1px solid rgba(81, 104, 255, 0.1)',
                      }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                        Question {currentQuestionIndex + 1} of {currentQuiz.questions.length}
                      </Typography>
                      
                      {/* Question Navigation */}
                      <Box sx={{
                        display: 'block',
                        maxWidth: '100%',
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        mb: 2,
                        px: 1,
                        scrollbarColor: '#5168ff #222',
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': {
                          height: 8,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#0a0d14',
                          borderRadius: 4,
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'rgba(81, 104, 255, 0.05)',
                        },
                      }}>
                        {currentQuiz.questions.map((q, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            variant={idx === currentQuestionIndex ? 'contained' : 'outlined'}
                            color={idx === currentQuestionIndex ? 'primary' : 'inherit'}
                            onClick={() => {
                              setCurrentQuestionIndex(idx);
                              setSelectedAnswer(null);
                              setShowExplanation(false);
                            }}
                            sx={{
                              minWidth: 36,
                              borderRadius: '50%',
                              fontWeight: idx === currentQuestionIndex ? 700 : 400,
                              display: 'inline-block',
                              mx: 0.5,
                              bgcolor:
                                quizAnswers[idx] === true
                                  ? 'rgba(76, 175, 80, 0.5)'
                                  : quizAnswers[idx] === false
                                  ? 'rgba(244, 67, 54, 0.5)'
                                  : undefined,
                              borderColor:
                                quizAnswers[idx] === true
                                  ? '#15905f'
                                  : quizAnswers[idx] === false
                                  ? '#c24150'
                                  : undefined,
                              color:
                                quizAnswers[idx] === true
                                  ? 'text.primary'
                                  : quizAnswers[idx] === false
                                  ? 'text.primary'
                                  : undefined,
                            }}
                          >
                            {idx + 1}
                          </Button>
                        ))}
                      </Box>
                      
                      <Typography variant="body1" sx={{ color: 'text.primary', mb: 3 }}>
                        {currentQuestion.question}
                      </Typography>

                      <RadioGroup 
                        value={selectedAnswer} 
                        onChange={handleAnswerSelect}
                        disabled={quizAnswers[currentQuestionIndex] !== null}
                      >
                        {currentQuestion.options.map((option, index) => (
                          <FormControlLabel
                            key={index}
                            value={index}
                            control={
                              <Radio 
                                sx={{
                                  color: quizAnswers[currentQuestionIndex] !== null ? '#bbb' : '#5168ff',
                                  '&.Mui-checked': {
                                    color: showExplanation && index === currentQuestion.correctAnswer ? '#15905f' :
                                           showExplanation && selectedAnswer === index && index !== currentQuestion.correctAnswer ? '#c24150' :
                                           quizAnswers[currentQuestionIndex] !== null ? '#bbb' : '#5168ff',
                                  },
                                  cursor: quizAnswers[currentQuestionIndex] !== null ? 'not-allowed' : 'pointer',
                                  opacity: quizAnswers[currentQuestionIndex] !== null ? 0.7 : 1,
                                }}
                              />
                            }
                            label={option}
                            disabled={quizAnswers[currentQuestionIndex] !== null}
                            sx={{ 
                              color: quizAnswers[currentQuestionIndex] !== null ? '#bbb' : 'text.primary',
                              opacity: quizAnswers[currentQuestionIndex] !== null ? 0.7 : 1,
                              cursor: quizAnswers[currentQuestionIndex] !== null ? 'not-allowed' : 'pointer',
                              backgroundColor: quizAnswers[currentQuestionIndex] !== null ? 'rgba(200,200,200,0.08)' : 'transparent',
                              borderRadius: 1,
                              transition: 'background 0.2s, color 0.2s, opacity 0.2s',
                              // Keep correct/incorrect highlight if showing explanation
                              ...(showExplanation && index === currentQuestion.correctAnswer && {
                                color: '#15905f',
                                fontWeight: 700,
                              }),
                              ...(showExplanation && selectedAnswer === index && index !== currentQuestion.correctAnswer && {
                                color: '#c24150',
                                fontWeight: 700,
                              })
                            }}
                          />
                        ))}
                      </RadioGroup>

                      {showExplanation && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(81, 104, 255, 0.1)', borderRadius: 1 }}>
                          <Typography sx={{ color: '#5168ff' }}>
                            {currentQuestion.explanation}
                          </Typography>
                        </Box>
                      )}

                      {/* Submit/Next logic */}
                      {!showExplanation && (
                        <Button 
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={selectedAnswer === null || quizAnswers[currentQuestionIndex] !== null}
                          sx={{ mt: 3 }}
                        >
                          Submit Answer
                        </Button>
                      )}
                      {/* Next Question button (not on last question) */}
                      {currentQuestionIndex < currentQuiz.questions.length - 1 && (
                        <Button
                          variant="contained"
                          sx={{ mt: 3, ml: 2 }}
                          onClick={() => {
                            setCurrentQuestionIndex(currentQuestionIndex + 1);
                            setSelectedAnswer(null);
                            setShowExplanation(false);
                          }}
                        >
                          Next Question
                        </Button>
                      )}
                      {/* Submit Quiz button: show on all questions if all are answered */}
                      {quizAnswers.every(ans => ans !== null) && (
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          sx={{ mt: 3, fontWeight: 700, fontSize: '1.1rem' }}
                          onClick={() => setQuizCompleted(true)}
                        >
                          Submit Quiz
                        </Button>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          ) : (

            <>
              {/* Progress Section */}
              <Paper elevation={0} sx={{ p: 3, mb: 4 }}>
                {/* Overall Progress */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary' }}>
                      Total Progress
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#5168ff' }}>
                      {progressStats.completed} / {progressStats.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(progressStats.completed / progressStats.total) * 100}
                    sx={{ 
                      height: 10, 
                      borderRadius: 999,
                      backgroundColor: 'rgba(81, 104, 255, 0.12)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #5168ff, #73d5ff)',
                        borderRadius: 999,
                      }
                    }} 
                  />
                </Box>

                {/* Difficulty Progress */}
                <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
                  {Object.entries(progressStats.difficultyStats).map(([difficulty, stats]) => (
                    <Box key={difficulty} sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ 
                          color: difficulty === 'Easy' ? '#15905f' : 
                                 difficulty === 'Medium' ? '#b7791f' : '#c24150'
                        }}>
                          {difficulty}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {stats.completed} / {stats.total}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(stats.completed / stats.total) * 100}
                        sx={{ 
                          height: 6, 
                          borderRadius: 999,
                          backgroundColor: difficulty === 'Easy' ? 'rgba(0, 255, 0, 0.2)' : 
                                          difficulty === 'Medium' ? 'rgba(255, 153, 0, 0.2)' : 
                                          'rgba(255, 0, 0, 0.2)',
                          border: `1px solid ${
                            difficulty === 'Easy' ? 'rgba(0, 255, 0, 0.3)' : 
                            difficulty === 'Medium' ? 'rgba(255, 153, 0, 0.3)' : 
                            'rgba(255, 0, 0, 0.3)'
                          }`,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: difficulty === 'Easy' ? '#15905f' : 
                                            difficulty === 'Medium' ? '#b7791f' : '#c24150'
                          }
                        }} 
                      />
                    </Box>
                  ))}
                </Box>

                {/* Topic Progress */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 0 }}>
                    Topic Progress
                  </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setTopicProgressOpen((open) => !open)}
                      sx={{ ml: 1, color: 'text.primary' }}
                      aria-label={topicProgressOpen ? 'Collapse' : 'Expand'}
                    >
                      {topicProgressOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={topicProgressOpen}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {Object.entries(progressStats.topicStats)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([topicId, stats]) => (
                        <Box key={topicId} sx={{ width: 'calc(33.33% - 16px)', minWidth: 250 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                              {stats.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#5168ff', ml: 1 }}>
                              {stats.completed} / {stats.total}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={(stats.completed / stats.total) * 100}
                            sx={{ 
                              height: 4, 
                              borderRadius: 999,
                              backgroundColor: 'rgba(81, 104, 255, 0.12)',
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #5168ff, #73d5ff)',
                                borderRadius: 999,
                              }
                            }} 
                          />
                        </Box>
                    ))}
                  </Box>
                  </Collapse>
                </Box>
              </Paper>

              {!selectedTopic && (
              <Paper 
                sx={{ 
                  p: 4,
                  bgcolor: 'rgba(81, 104, 255, 0.05)',
                  border: '1px solid rgba(81, 104, 255, 0.1)',
                }}
              >
                <Typography variant="h5" sx={{ color: 'text.primary' }}>
                  Select a topic from the sidebar to view problems and quizzes
                </Typography>
              </Paper>
            )}
          
              {/* Existing Problems Table */}
              {!isQuizMode && selectedTopic && quizzesByTopic[selectedTopic] && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    fullWidth
                    variant="contained"
                  onClick={() => handleStudyAll(selectedTopic)}
                  sx={{
                      p: 2,
                    }}
                  >
                    Study All {quizzesByTopic[selectedTopic]?.title} Questions
                  </Button>
                </Box>
              )}
              {selectedTopic && (
              <Paper 
                sx={{ 
                  bgcolor: 'rgba(81, 104, 255, 0.05)',
                  border: '1px solid rgba(81, 104, 255, 0.1)',
                }}
              >
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={50}></TableCell>
                        <TableCell>Problem</TableCell>
                        <TableCell>LeetCode</TableCell>
                        <TableCell>Difficulty</TableCell>
                        <TableCell>Quiz</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quizzesByTopic[selectedTopic]?.problems.map((problem) => (
                        <ProblemRow 
                          key={problem.id} 
                          problem={problem}
                          topicId={selectedTopic}
                          isCompleted={completedSections[`${selectedTopic}-${problem.id}`]}
                          onToggle={() => handleSectionToggle(selectedTopic, problem.id)}
                          onStartQuiz={handleStartQuiz}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

export default Quizzes; 
